import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PricingEditor from '../components/PricingEditor';
import FeedProcessorPanel from '../components/FeedProcessorPanel';
import TrackMaintenance from '../components/TrackMaintenance';
import LiveOps from '../components/LiveOps';
import WidgetClients from '../components/WidgetClients';
import ContactMessages from '../components/ContactMessages';
import SignalEffects from '../components/SignalEffects';
import { RoiCell } from '../components/RoiCell';
import { AutoTriLogPanel } from '../components/AutoTriLogPanel';
import { AvoidListCard } from '../components/AvoidListCard';
import { GoodTrainersCard } from '../components/GoodTrainersCard';
import { AlertEmailsBox } from '../components/AlertEmailsBox';
import {
  gradeTicket, groupSummary,
  capToRecent7Days, formatLocalDateTime,
} from '../lib/downloadGrading';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ET calendar date (YYYY-MM-DD) offset by `days` from today. Used to default the
// Jobs-page audit-log date ranges to yesterday -> today.
const etDate = (days = 0) => {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  et.setDate(et.getDate() + days);
  return `${et.getFullYear()}-${String(et.getMonth() + 1).padStart(2, '0')}-${String(et.getDate()).padStart(2, '0')}`;
};

// Extract a readable message from a FastAPI error body (detail may be a string,
// an object, or an array of {msg} validation errors).
const formatApiError = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msg = detail.map((e) => (e && e.msg) || (typeof e === 'string' ? e : '')).filter(Boolean).join('; ');
    return msg || fallback;
  }
  if (typeof detail === 'object') return detail.msg || fallback;
  return fallback;
};

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [affiliateFilter, setAffiliateFilter] = useState('all');
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'affiliates', or 'downloads'
  
  // Downloads state
  const [downloads, setDownloads] = useState([]);
  const [totalDownloads, setTotalDownloads] = useState(0);
  const [loadingDownloads, setLoadingDownloads] = useState(false);
  const [downloadUserFilter, setDownloadUserFilter] = useState('all'); // user_id or 'all'
  const [expandedDownload, setExpandedDownload] = useState(null); // download id
  const [downloadDetails, setDownloadDetails] = useState({}); // id -> {loading, graded, wagered, returned, roi, rows}
  const [poolGrading, setPoolGrading] = useState({ loading: false, byPool: {}, ungraded: 0 }); // $$ payouts + ROI by pool
  const [downloadSummaryMode, setDownloadSummaryMode] = useState('pool'); // 'pool' | 'track' | 'trackpool'
  const [payoutsByRace, setPayoutsByRace] = useState({}); // snapshot of payoutsCacheRef for grouping recompute
  const payoutsCacheRef = useRef({}); // race_key -> payouts[] (avoids refetch across renders/filters)
  const [downloadStartDate, setDownloadStartDate] = useState(() => {
    // After 12:00 PM EST, default the race-date range to today only; otherwise
    // keep the prior behavior (yesterday → today).
    const estHour = parseInt(new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour12: false, hour: '2-digit' }).format(new Date()), 10) % 24;
    const d = new Date();
    if (estHour < 12) d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [downloadEndDate, setDownloadEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Generated Tix (exacta sim) state
  const [gtStart, setGtStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); });
  const [gtEnd, setGtEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [gtSummary, setGtSummary] = useState(null);
  const [rankPerf, setRankPerf] = useState(null);
  const [showOverbet, setShowOverbet] = useState(false);
  const [overbetDetail, setOverbetDetail] = useState(null);
  const [gtLoading, setGtLoading] = useState(false);
  const [gtRunning, setGtRunning] = useState(false);
  const [gtRecompute, setGtRecompute] = useState(false);
  const [gtRunMsg, setGtRunMsg] = useState('');
  const [gtDirty, setGtDirty] = useState(false); // dates changed but not yet Applied
  const [perfBusy, setPerfBusy] = useState(false);
  const [perfMsg, setPerfMsg] = useState('');
  const [gtExpandedTrack, setGtExpandedTrack] = useState(null);
  // Strong Signals drill-down
  const [gtCombo, setGtCombo] = useState(null);            // selected {rule_set, algo} for By-Track
  const [gtByTrack, setGtByTrack] = useState([]);
  const [gtByTrackLoading, setGtByTrackLoading] = useState(false);
  const [ssExpandedKey, setSsExpandedKey] = useState(null); // `${algo}|${scope}`
  const [ssDetailMap, setSsDetailMap] = useState({});       // key -> detail
  const [ssDetailLoading, setSsDetailLoading] = useState(false);
  
  // TP Job state
  const [runningTPJob, setRunningTPJob] = useState(false);
  const [tpJobResult, setTpJobResult] = useState(null);
  const [tpForce, setTpForce] = useState(false);
  const [tpDate, setTpDate] = useState('');
  
  // Edit user modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [editTrialDate, setEditTrialDate] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  
  // Affiliate modal state
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [affiliateAction, setAffiliateAction] = useState('assign'); // 'assign' or 'link'
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAffid, setSelectedAffid] = useState('');
  
  // Create affiliate modal
  const [showCreateAffiliateModal, setShowCreateAffiliateModal] = useState(false);
  const [newAffiliate, setNewAffiliate] = useState({ affid: '', name: '', commission_pct: 10, contact_email: '' });
  const [showEditAffiliateModal, setShowEditAffiliateModal] = useState(false);
  const [editAffiliate, setEditAffiliate] = useState({ affid: '', name: '', commission_pct: 0, contact_email: '' });
  
  // User detail panel
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loginEvents, setLoginEvents] = useState([]);
  const [loadingLoginEvents, setLoadingLoginEvents] = useState(false);
  const [txns, setTxns] = useState(null);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [affSummary, setAffSummary] = useState(null);
  const [showAffSummary, setShowAffSummary] = useState(false);
  const [loadingAffSummary, setLoadingAffSummary] = useState(false);
  const [sortKey, setSortKey] = useState('last_login');
  const [sortDir, setSortDir] = useState('desc');
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Refresh unread contact-message count on mount and whenever tabs change
  // (reading a message marks it read, so the badge updates when returning).
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API_URL}/api/contact/admin/messages`, { credentials: 'include' });
        if (res.ok) { const d = await res.json(); setUnreadMessages(d.new_count || 0); }
      } catch { /* noop */ }
    };
    fetchUnread();
  }, [activeTab]);
  
  // TP Job last run
  const [tpLastRun, setTpLastRun] = useState(null);

  // Feed worker status (for the Jobs tab health card)
  const [feedWorker, setFeedWorker] = useState(null);
  const [loadingFeedWorker, setLoadingFeedWorker] = useState(false);

  // Scratch sweep job status (Jobs tab)
  const [scratchStatus, setScratchStatus] = useState(null);
  const [loadingScratch, setLoadingScratch] = useState(false);
  const [runningScratch, setRunningScratch] = useState(false);

  // Pool Minimums (PoolMinDefaults) upload (Jobs tab)
  const [poolMinStatus, setPoolMinStatus] = useState(null);
  const [poolMinResult, setPoolMinResult] = useState(null);
  const [uploadingPoolMin, setUploadingPoolMin] = useState(false);

  // CSR report upload + Auto Win-Bet (Jobs tab)
  const [csrStatus, setCsrStatus] = useState(null);
  const [uploadingCsr, setUploadingCsr] = useState(false);
  const [autobet, setAutobet] = useState(null);        // full status payload
  const [autobetDay, setAutobetDay] = useState(null);  // selected race-day for the ledger
  const [autobetCfg, setAutobetCfg] = useState(null);  // editable config
  const [autobetOpen, setAutobetOpen] = useState(false);  // Auto Win-Bet card collapsed by default
  const [savingAutobet, setSavingAutobet] = useState(false);
  const [condsOpen, setCondsOpen] = useState(true);
  const [autotriCfg, setAutotriCfg] = useState(null);     // Auto Tri Bet config
  const [k3cfg, setK3cfg] = useState(null);               // K3-demotion rule (read-only)
  const [aspCfg, setAspCfg] = useState(null);             // Live Sent to ASP Post config
  const [aspIp, setAspIp] = useState('');                 // caller's detected IP
  const [aspRecent, setAspRecent] = useState([]);         // ADW audit log
  const [aspNewIp, setAspNewIp] = useState('');
  const [savingAutotri, setSavingAutotri] = useState(false);
  const [autotriLogOpen, setAutotriLogOpen] = useState(false); // accordion (collapsed by default)
  const [aspLogOpen, setAspLogOpen] = useState(false);         // accordion (collapsed by default)
  // Audit-log date ranges (Jobs page) — default yesterday -> today (ET)
  const [aspFrom, setAspFrom] = useState(etDate(-1));
  const [aspTo, setAspTo] = useState(etDate(0));
  const [aspFromD, setAspFromD] = useState(etDate(-1));
  const [aspToD, setAspToD] = useState(etDate(0));

  // Global ticker display setting (authoritative for all visitors)
  const [tickerSetting, setTickerSetting] = useState(null);
  const [savingTicker, setSavingTicker] = useState(false);

  // Embeddable widget feature flags (admin-controlled)
  const [widgetSettings, setWidgetSettings] = useState(null);
  const [savingWidget, setSavingWidget] = useState(false);
  const [exactaEvMode, setExactaEvMode] = useState(null);
  const [savingExactaEv, setSavingExactaEv] = useState(false);
  
  // Admin Stats state
  const [adminStats, setAdminStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsStartDate, setStatsStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  });
  const [statsEndDate, setStatsEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/users`, { credentials: 'include' });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAffiliates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/affiliates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch affiliates:', err);
    }
  }, []);

  const fetchDownloads = useCallback(async () => {
    setLoadingDownloads(true);
    try {
      const params = new URLSearchParams();
      if (downloadStartDate) params.set('start_date', downloadStartDate);
      if (downloadEndDate) params.set('end_date', downloadEndDate);
      const response = await fetch(`${API_URL}/api/quickpick/download-stats?${params.toString()}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDownloads(data.recent || []);
        setTotalDownloads(data.total_downloads || 0);
      }
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
    } finally {
      setLoadingDownloads(false);
    }
  }, [downloadStartDate, downloadEndDate]);

  // Expand a download row → fetch that race's payouts and grade the recorded tickets.
  const toggleDownloadDetail = useCallback(async (d) => {
    if (expandedDownload === d.id) { setExpandedDownload(null); return; }
    setExpandedDownload(d.id);
    if (downloadDetails[d.id]) return; // cached
    const tickets = d.tickets || [];
    if (!tickets.length) {
      setDownloadDetails((prev) => ({ ...prev, [d.id]: { loading: false, noTickets: true } }));
      return;
    }
    setDownloadDetails((prev) => ({ ...prev, [d.id]: { loading: true } }));
    let payouts = [];
    try {
      if (d.race_date) {
        const r = await fetch(
          `${API_URL}/api/quickpick/entries?track=${encodeURIComponent(d.track)}&race=${d.race_number}&date=${d.race_date}`,
          { credentials: 'include' }
        );
        if (r.ok) { const j = await r.json(); payouts = j.payouts || []; }
      }
    } catch (e) { /* graded stays false */ }
    const graded = payouts.length > 0;
    const wagered = tickets.reduce((s, t) => s + (t.amount || 0), 0);
    const rows = tickets.map((t) => {
      const ret = graded ? gradeTicket(t, payouts) : 0;
      return { pool: t.pool, combo: t.combo, amount: t.amount || 0, ret, win: ret > 0 };
    });
    const returned = rows.reduce((s, x) => s + x.ret, 0);
    const roi = wagered > 0 ? ((returned - wagered) / wagered) * 100 : 0;
    setDownloadDetails((prev) => ({ ...prev, [d.id]: { loading: false, graded, wagered, returned, roi, rows } }));
  }, [expandedDownload, downloadDetails]);

  useEffect(() => {
    fetchUsers();
    fetchAffiliates();
  }, [fetchUsers, fetchAffiliates]);

  useEffect(() => {
    if (activeTab === 'downloads') {
      fetchDownloads();
    }
  }, [activeTab, fetchDownloads]);

  // Downloads currently visible after the user filter, and $ totals per format.
  const visibleDownloads = useMemo(() =>
    downloadUserFilter === 'all' ? downloads : downloads.filter((d) => d.user_id === downloadUserFilter),
    [downloads, downloadUserFilter]);
  const formatTotals = useMemo(() => {
    const m = {};
    visibleDownloads.forEach((d) => { const f = d.format || 'other'; m[f] = (m[f] || 0) + (d.total_wager || 0); });
    return m;
  }, [visibleDownloads]);

  // Fetch every visible download's race payouts into the cache, then snapshot it so
  // the grouped summary (by pool / track / track+pool) recomputes via useMemo.
  const gradeAllDownloads = useCallback(async () => {
    const all = visibleDownloads;
    if (!all.length) { setPoolGrading({ loading: false }); setPayoutsByRace({ ...payoutsCacheRef.current }); return; }
    setPoolGrading({ loading: true });
    const gradable = all.filter((d) => (d.tickets || []).length && d.race_date);
    const races = {};
    gradable.forEach((d) => { races[`${d.track}|${d.race_number}|${d.race_date}`] = d; });
    await Promise.all(
      Object.keys(races)
        .filter((k) => !(k in payoutsCacheRef.current))
        .map(async (k) => {
          const d = races[k];
          try {
            const r = await fetch(
              `${API_URL}/api/quickpick/entries?track=${encodeURIComponent(d.track)}&race=${d.race_number}&date=${d.race_date}`,
              { credentials: 'include' }
            );
            payoutsCacheRef.current[k] = r.ok ? ((await r.json()).payouts || []) : [];
          } catch (e) { payoutsCacheRef.current[k] = []; }
        })
    );
    setPayoutsByRace({ ...payoutsCacheRef.current });
    setPoolGrading({ loading: false });
  }, [visibleDownloads]);

  // Grouped summary (mode-aware) — recomputes when the mode or graded payouts change.
  const downloadSummary = useMemo(
    () => groupSummary(visibleDownloads, downloadSummaryMode, payoutsByRace),
    [visibleDownloads, downloadSummaryMode, payoutsByRace]
  );

  // 7-day cap on the DETAIL table (summary reflects the full range).
  const { rows: cappedDownloads, capped: downloadsCapped, cutoff: downloadsCutoff } = useMemo(
    () => capToRecent7Days(visibleDownloads), [visibleDownloads]
  );

  useEffect(() => {
    if (activeTab === 'downloads') gradeAllDownloads();
  }, [activeTab, gradeAllDownloads]);

  // Generated Tix: fetch summary, run sim, download CSV
  const fetchByTrack = useCallback(async (rule_set, algo) => {
    setGtByTrackLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('rule_set', rule_set); p.set('algo', algo);
      if (gtStart) p.set('start_date', gtStart);
      if (gtEnd) p.set('end_date', gtEnd);
      const r = await fetch(`${API_URL}/api/quickpick/generated-tix/by-track?${p}`, { credentials: 'include' });
      if (r.ok) { const d = await r.json(); setGtByTrack(d.by_track || []); }
    } catch (e) { /* noop */ } finally { setGtByTrackLoading(false); }
  }, [gtStart, gtEnd]);

  const fetchGtSummary = useCallback(async () => {
    setGtLoading(true);
    try {
      const p = new URLSearchParams();
      if (gtStart) p.set('start_date', gtStart);
      if (gtEnd) p.set('end_date', gtEnd);
      const r = await fetch(`${API_URL}/api/quickpick/generated-tix/summary?${p}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setGtSummary(data);
        // default By-Track combo = live config
        const live = data.live || (data.matrix && data.matrix[0]);
        if (live) { setGtCombo({ rule_set: live.rule_set, algo: live.algo }); fetchByTrack(live.rule_set, live.algo); }
      }
      setSsExpandedKey(null); setSsDetailMap({}); setGtExpandedTrack(null);
      setShowOverbet(false); setOverbetDetail(null);
      try {
        const rp = await fetch(`${API_URL}/api/quickpick/generated-tix/rank-performance?${p}`, { credentials: 'include' });
        if (rp.ok) setRankPerf(await rp.json());
      } catch (e) { /* noop */ }
    } catch (e) { /* noop */ } finally { setGtLoading(false); }
  }, [gtStart, gtEnd, fetchByTrack]);

  const loadOverbetDetail = useCallback(async () => {
    const p = new URLSearchParams();
    if (gtStart) p.set('start_date', gtStart);
    if (gtEnd) p.set('end_date', gtEnd);
    const r = await fetch(`${API_URL}/api/quickpick/generated-tix/overbet-detail?${p}`, { credentials: 'include' });
    if (r.ok) setOverbetDetail((await r.json()).rows);
  }, [gtStart, gtEnd]);

  const toggleOverbet = () => {
    const next = !showOverbet;
    setShowOverbet(next);
    if (next && overbetDetail === null) loadOverbetDetail();
  };

  const selectCombo = (rule_set, algo) => {
    setGtCombo({ rule_set, algo });
    setGtExpandedTrack(null);
    fetchByTrack(rule_set, algo);
  };

  const applyGtRange = useCallback(() => { setGtDirty(false); fetchGtSummary(); }, [fetchGtSummary]);

  const downloadPerfReport = async () => {
    setPerfBusy(true);
    setPerfMsg(`Building performance PDF for ${gtStart} → ${gtEnd}… (up to ~2 min)`);
    try {
      const p = new URLSearchParams({ start_date: gtStart, end_date: gtEnd });
      const r = await fetch(`${API_URL}/api/quickpick/generated-tix/performance-report?${p}`,
        { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.job_id) { setPerfMsg(data?.detail || 'Could not start report.'); setPerfBusy(false); return; }
      const jobId = data.job_id;
      const started = Date.now();
      while (Date.now() - started < 5 * 60 * 1000) {
        await new Promise((res) => setTimeout(res, 2500));
        let job = null;
        try {
          const sr = await fetch(`${API_URL}/api/quickpick/generated-tix/performance-report-status/${jobId}`, { credentials: 'include' });
          job = await sr.json().catch(() => null);
        } catch (e) { continue; }
        if (!job) continue;
        if (job.status === 'running') {
          setPerfMsg(`Building performance PDF for ${gtStart} → ${gtEnd}… (up to ~2 min)`);
        } else if (job.status === 'done') {
          const dl = await fetch(`${API_URL}/api/quickpick/generated-tix/performance-report-download/${jobId}`, { credentials: 'include' });
          const blob = await dl.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `intellitix_performance_${gtStart}_${gtEnd}.pdf`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          setPerfMsg(`Report ready — downloaded intellitix_performance_${gtStart}_${gtEnd}.pdf`);
          break;
        } else if (job.status === 'error') {
          setPerfMsg(`Report failed: ${job.error || 'unknown'}`);
          break;
        }
      }
    } catch (e) {
      setPerfMsg('Report failed.');
    } finally { setPerfBusy(false); }
  };

  // Auto-fetch ONLY when the tab is opened — never on date change (changing one
  // date would otherwise fire a fetch with the stale other date). A ref keeps the
  // latest fetcher without re-triggering the effect when gtStart/gtEnd change.
  const gtSummaryRef = useRef(fetchGtSummary);
  gtSummaryRef.current = fetchGtSummary;
  useEffect(() => { if (activeTab === 'gentix') { setGtDirty(false); gtSummaryRef.current(); } }, [activeTab]);

  const runGtSim = async () => {
    setGtRunning(true);
    setGtRunMsg('');
    // Aggregate the per-day diagnostics (why races drop / fail to grade).
    const diagFromDays = (days) => {
      const skips = {}; const fails = {}; let eligible = 0; let graded = 0;
      for (const d of (days || [])) {
        eligible += d.eligible_races || 0;
        graded += d.races || 0;
        for (const [k, v] of Object.entries(d.skips || {})) skips[k] = (skips[k] || 0) + v;
        for (const [k, v] of Object.entries(d.fail_reasons || {})) fails[k] = (fails[k] || 0) + v;
      }
      const fmt = (o) => Object.entries(o).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
      return { line: `Eligible ${eligible} · graded ${graded} · ungraded ${eligible - graded}`,
               fails: `Ungraded reasons — ${fmt(fails)}`, skips: `Pre-filter drops — ${fmt(skips)}` };
    };
    const finalMsg = (summary) => {
      const dg = diagFromDays(summary.results);
      return `Ran ${summary.ran} day(s), skipped ${summary.skipped} already-computed, ${summary.total_races} graded races (${summary.range.start} → ${summary.range.end}).\n${dg.line}\n${dg.fails}\n${dg.skips}`;
    };
    try {
      const p = new URLSearchParams();
      if (gtStart) p.set('start_date', gtStart);
      if (gtEnd) p.set('end_date', gtEnd);
      if (gtRecompute) p.set('recompute', 'true');
      p.set('background', 'true'); // run as a pollable job so the request never hits the edge timeout
      const r = await fetch(`${API_URL}/api/quickpick/generated-tix/run?${p}`, { method: 'POST', credentials: 'include' });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data?.job_id) {
        setGtRunMsg(data?.detail || 'Run failed to start.');
        setGtRunning(false);
        return;
      }
      // Poll the job to completion — each request is tiny, so no timeout regardless of range/CPU.
      const jobId = data.job_id;
      const started = Date.now();
      while (Date.now() - started < 20 * 60 * 1000) { // 20-min safety cap
        await new Promise((res) => setTimeout(res, 2500));
        let job = null;
        try {
          const sr = await fetch(`${API_URL}/api/quickpick/generated-tix/run-status/${jobId}`, { credentials: 'include' });
          job = await sr.json().catch(() => null);
          if (!sr.ok) { setGtRunMsg(job?.detail || 'Lost the recompute job.'); break; }
        } catch (e) { continue; } // transient network blip during polling — keep trying
        if (!job) continue;
        const prog = job.progress || {};
        if (job.status === 'running') {
          setGtRunMsg(`Recomputing… day ${prog.done || 0} of ${prog.total || '?'} (${job.range?.start} → ${job.range?.end}). This runs in the background — safe to wait.`);
        } else if (job.status === 'done') {
          setGtRunMsg(finalMsg(job.summary));
          setGtDirty(false);
          await fetchGtSummary();
          break;
        } else if (job.status === 'error') {
          setGtRunMsg(`Recompute errored: ${job.error || 'unknown'}`);
          break;
        }
      }
    } catch (e) {
      setGtRunMsg('Run failed.');
    } finally { setGtRunning(false); }
  };

  const downloadGtCsv = () => {
    const p = new URLSearchParams();
    if (gtStart) p.set('start_date', gtStart);
    if (gtEnd) p.set('end_date', gtEnd);
    if (gtCombo) { p.set('rule_set', gtCombo.rule_set); p.set('algo', gtCombo.algo); }
    window.open(`${API_URL}/api/quickpick/generated-tix/csv?${p}`, '_blank');
  };

  const toggleStrongRow = async (algo, scope) => {
    const key = `${algo}|${scope}`;
    if (ssExpandedKey === key) { setSsExpandedKey(null); return; }
    setSsExpandedKey(key);
    if (!ssDetailMap[key]) {
      setSsDetailLoading(true);
      try {
        const p = new URLSearchParams();
        p.set('algo', algo); p.set('scope', scope);
        if (gtStart) p.set('start_date', gtStart);
        if (gtEnd) p.set('end_date', gtEnd);
        const r = await fetch(`${API_URL}/api/quickpick/generated-tix/strong-detail?${p}`, { credentials: 'include' });
        if (r.ok) { const d = await r.json(); setSsDetailMap((m) => ({ ...m, [key]: d })); }
      } catch (e) { /* noop */ } finally { setSsDetailLoading(false); }
    }
  };
  const algoLabel = (a) => (a === 'logarithmic' ? 'Logarithmic' : a === 'hybrid' ? 'Hybrid (by field size)' : 'Original');

  // Filter users by search (email or name) and status
  const filteredUsers = useMemo(() => {
    const filtered = users.filter(u => {
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || 
        u.email?.toLowerCase().includes(searchLower) ||
        u.name?.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === 'all' || u.subscription_status === statusFilter;
      const matchesAffiliate = affiliateFilter === 'all' || u.referred_by === affiliateFilter;
      return matchesSearch && matchesStatus && matchesAffiliate;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (u) => {
      if (sortKey === 'last_login' || sortKey === 'created_at') {
        return u[sortKey] ? new Date(u[sortKey]).getTime() : 0;
      }
      return (u[sortKey] || '').toString().toLowerCase();
    };
    return [...filtered].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [users, search, statusFilter, affiliateFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'last_login' || key === 'created_at' ? 'desc' : 'asc');
    }
  };
  const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  // Filter affiliates by search (affid, name, or email)
  const filteredAffiliates = useMemo(() => {
    const searchLower = affiliateSearch.toLowerCase();
    return affiliates.filter(a => {
      return !affiliateSearch ||
        a.affid?.toLowerCase().includes(searchLower) ||
        a.name?.toLowerCase().includes(searchLower) ||
        a.contact_email?.toLowerCase().includes(searchLower);
    });
  }, [affiliates, affiliateSearch]);

  // Run Top Performers Job
  const runTPJob = async (raceDate = null, forceReprocess = false) => {
    setRunningTPJob(true);
    setTpJobResult(null);
    try {
      const params = new URLSearchParams();
      if (raceDate) params.set('race_date', raceDate);
      if (forceReprocess) params.set('force_reprocess', 'true');
      const qs = params.toString();
      const url = `${API_URL}/api/quickpick/process-completed${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, { 
        method: 'POST',
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        const r = data.result || {};
        setTpJobResult(r);
        // Richer, non-confusing message
        const parts = [
          `${r.new ?? r.processed ?? 0} new`,
          `${r.skipped_existing ?? 0} already processed`,
          `${r.winners ?? 0} winners`,
        ];
        if (r.skipped_no_data) parts.push(`${r.skipped_no_data} excluded (no data)`);
        if (r.deleted) parts.unshift(`${r.deleted} cleared`);
        const ruleTxt = r.rule_set ? ` (Rule ${r.rule_set})` : '';
        if ((r.races_available ?? 0) === 0) {
          setSuccess(`TP Job${ruleTxt}: no completed races available for this date yet.`);
        } else {
          setSuccess(`TP Job${ruleTxt}: ${parts.join(', ')}.`);
        }
        // Refresh last run time
        fetchTpLastRun();
      } else {
        setError('Failed to run TP job');
      }
    } catch (err) {
      setError('Failed to run TP job: ' + err.message);
    } finally {
      setRunningTPJob(false);
    }
  };
  
  // Fetch TP Job last run time
  const fetchTpLastRun = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quickpick/tp-last-run`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTpLastRun(data);
      }
    } catch (err) {
      console.error('Failed to fetch TP last run:', err);
    }
  };
  
  // Fetch TP last run on mount
  useEffect(() => {
    fetchTpLastRun();
  }, []);

  // Feed worker status (lock holder + enabled + last run) for the Jobs tab
  const fetchFeedWorker = useCallback(async () => {
    setLoadingFeedWorker(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/status`, { credentials: 'include' });
      if (res.ok) setFeedWorker(await res.json());
    } catch (err) {
      console.error('Failed to fetch feed worker status:', err);
    } finally {
      setLoadingFeedWorker(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'jobs') fetchFeedWorker();
  }, [activeTab, fetchFeedWorker]);

  // Scratch sweep status (Equibase late-changes) for the Jobs tab
  const fetchScratchStatus = useCallback(async () => {
    setLoadingScratch(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/scratch-status`, { credentials: 'include' });
      if (res.ok) setScratchStatus((await res.json()).last_run || {});
    } catch (err) {
      console.error('Failed to fetch scratch status:', err);
    } finally {
      setLoadingScratch(false);
    }
  }, []);

  const runScratchNow = async () => {
    setRunningScratch(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/scratch-run-now`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const r = data.result || {};
        setSuccess(`Scratch sweep: ${r.tracks || 0} tracks, ${r.scratches_found || 0} scratches found, ${r.entries_updated || 0} entries updated.`);
        fetchScratchStatus();
      } else {
        setError('Failed to run scratch sweep');
      }
    } catch (err) {
      setError('Failed to run scratch sweep: ' + err.message);
    } finally {
      setRunningScratch(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'jobs') fetchScratchStatus();
  }, [activeTab, fetchScratchStatus]);

  // Recompute prior dates (math + backtests) — Jobs tab
  const _today = new Date().toISOString().slice(0, 10);
  const [rcStart, setRcStart] = useState(_today);
  const [rcEnd, setRcEnd] = useState(_today);
  const [rcStatus, setRcStatus] = useState(null);
  const [rcStarting, setRcStarting] = useState(false);
  const rcPollRef = useRef(null);

  const fetchRecomputeStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/recompute-status`, { credentials: 'include' });
      if (res.ok) {
        const s = (await res.json()).status || {};
        setRcStatus(s);
        if (s.state === 'running' && !rcPollRef.current) {
          rcPollRef.current = setInterval(fetchRecomputeStatus, 3000);
        }
        if (s.state !== 'running' && rcPollRef.current) {
          clearInterval(rcPollRef.current); rcPollRef.current = null;
        }
      }
    } catch (err) { console.error('recompute-status failed', err); }
  }, []);

  const startRecompute = async () => {
    setRcStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/recompute?start=${rcStart}&end=${rcEnd}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(`Recompute started for ${data.start} → ${data.end} (${data.days} day${data.days > 1 ? 's' : ''}).`);
        fetchRecomputeStatus();
      } else {
        setError(data.detail || 'Failed to start recompute');
      }
    } catch (err) {
      setError('Failed to start recompute: ' + err.message);
    } finally {
      setRcStarting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'jobs') fetchRecomputeStatus();
    return () => { if (rcPollRef.current) { clearInterval(rcPollRef.current); rcPollRef.current = null; } };
  }, [activeTab, fetchRecomputeStatus]);

  // Pool Minimums (PoolMinDefaults) curated CSV for the Jobs tab
  const fetchPoolMinStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/pool-min-status`, { credentials: 'include' });
      if (res.ok) setPoolMinStatus((await res.json()).status || {});
    } catch (err) {
      console.error('Failed to fetch pool-min status:', err);
    }
  }, []);

  const uploadPoolMinCsv = async (file) => {
    if (!file) return;
    setUploadingPoolMin(true);
    setPoolMinResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/api/feed/pool-min-upload`, {
        method: 'POST', credentials: 'include', body: form,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPoolMinResult(data);
        setSuccess(`Pool Minimums updated: ${data.rows_parsed} rows parsed → ${data.resolved_entries} entries across ${data.tracks} tracks (${data.change_count} changed).`);
        fetchPoolMinStatus();
      } else {
        setError(data.detail || 'Failed to ingest pool minimums CSV');
      }
    } catch (err) {
      setError('Failed to upload pool minimums CSV: ' + err.message);
    } finally {
      setUploadingPoolMin(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'jobs') fetchPoolMinStatus();
  }, [activeTab, fetchPoolMinStatus]);

  // CSR report upload + Auto Win-Bet (Jobs tab)
  const fetchCsrStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/csr-status`, { credentials: 'include' });
      if (res.ok) setCsrStatus((await res.json()).status || {});
    } catch (err) { console.error('csr-status', err); }
  }, []);

  const fetchAutobet = useCallback(async (day) => {
    try {
      const q = day ? `?day=${encodeURIComponent(day)}` : '';
      const res = await fetch(`${API_URL}/api/feed/autobet-status${q}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAutobet(d); setAutobetCfg(d.config); setAutobetDay(d.day); }
    } catch (err) { console.error('autobet-status', err); }
  }, []);

  const uploadCsr = async (file) => {
    if (!file) return;
    setUploadingCsr(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/api/feed/csr-upload`, { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`CSR uploaded: ${data.rows} rows for ${(data.days || []).join(', ')} across ${(data.tracks || []).length} tracks.`);
        fetchCsrStatus();
      } else {
        setError(data.detail || 'Failed to ingest CSR CSV');
      }
    } catch (err) {
      setError('Failed to upload CSR: ' + err.message);
    } finally {
      setUploadingCsr(false);
    }
  };

  const saveAutobet = async (patch) => {
    setSavingAutobet(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/autobet-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (res.ok && data.success) { setAutobetCfg(data.config); fetchAutobet(); setSuccess('Auto Win-Bet settings saved.'); }
      else setError(data.detail || 'Failed to save Auto Win-Bet settings');
    } catch (err) {
      setError('Failed to save Auto Win-Bet settings: ' + err.message);
    } finally {
      setSavingAutobet(false);
    }
  };

  const fetchAutotri = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/autotri-config`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAutotriCfg(d.config); }
    } catch (err) { console.error('autotri-config', err); }
  }, [API_URL]);

  const fetchAsp = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/quickpick/adw-config`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAspCfg(d.config); setAspIp(d.your_ip || ''); }
    } catch (err) { console.error('adw-config', err); }
  }, [API_URL]);

  const fetchAspLog = useCallback(async (start, end) => {
    try {
      const res = await fetch(`${API_URL}/api/quickpick/adw-log?start=${start}&end=${end}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAspRecent(d.recent || []); }
    } catch (err) { console.error('adw-log', err); }
  }, [API_URL]);

  const fetchK3 = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/quickpick/k3-config`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setK3cfg(d.config); }
    } catch (err) { console.error('k3-config', err); }
  }, [API_URL]);

  const saveAsp = async (patch) => {
    try {
      const res = await fetch(`${API_URL}/api/quickpick/adw-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (res.ok && data.success) { setAspCfg(data.config); setAspIp(data.your_ip || ''); setSuccess('Live ASP Post settings saved.'); }
      else setError(data.detail || 'Failed to save Live ASP Post settings');
    } catch (err) { setError('Failed to save Live ASP Post settings: ' + err.message); }
  };

  const saveAutotri = async (patch) => {
    setSavingAutotri(true);
    try {
      const res = await fetch(`${API_URL}/api/feed/autotri-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (res.ok && data.success) { setAutotriCfg(data.config); setSuccess('Auto Tri Bet settings saved.'); }
      else setError(data.detail || 'Failed to save Auto Tri Bet settings');
    } catch (err) {
      setError('Failed to save Auto Tri Bet settings: ' + err.message);
    } finally {
      setSavingAutotri(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'jobs') { fetchCsrStatus(); fetchAutobet(); fetchAutotri(); fetchAsp(); fetchK3(); }
  }, [activeTab, fetchCsrStatus, fetchAutobet, fetchAutotri, fetchAsp, fetchK3]);

  useEffect(() => {
    if (activeTab === 'jobs') fetchAspLog(aspFrom, aspTo);
  }, [activeTab, fetchAspLog]); // eslint-disable-line react-hooks/exhaustive-deps


  // Global ticker display setting
  const fetchTickerSetting = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quickpick/ticker-setting`);
      if (response.ok) setTickerSetting(await response.json());
    } catch (err) {
      console.error('Failed to fetch ticker setting:', err);
    }
  };

  useEffect(() => {
    fetchTickerSetting();
  }, []);

  const updateTickerView = async (view) => {
    setSavingTicker(true);
    try {
      const response = await fetch(`${API_URL}/api/quickpick/ticker-setting?view=${view}`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (response.ok) {
        await fetchTickerSetting();
        setSuccess(`Ticker now showing: ${view === 'today' ? 'Today' : 'Yesterday'} (visible to all users).`);
      } else {
        setError('Failed to update ticker setting');
      }
    } catch (err) {
      setError('Failed to update ticker setting: ' + err.message);
    } finally {
      setSavingTicker(false);
    }
  };

  // Embeddable widget feature flags
  const fetchWidgetSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quickpick/widget-settings`);
      if (response.ok) setWidgetSettings(await response.json());
    } catch (err) {
      console.error('Failed to fetch widget settings:', err);
    }
  };

  // Exacta Expected-Value chiclet visibility mode
  const fetchExactaEvSetting = async () => {
    try {
      const r = await fetch(`${API_URL}/api/quickpick/exacta-ev-setting`);
      if (r.ok) setExactaEvMode((await r.json()).mode || 'entitled');
    } catch (err) { console.error('Failed to fetch Exacta EV setting:', err); }
  };
  const updateExactaEvSetting = async (mode) => {
    setSavingExactaEv(true);
    try {
      const r = await fetch(`${API_URL}/api/quickpick/exacta-ev-setting?mode=${mode}`, { method: 'PUT', credentials: 'include' });
      if (r.ok) { setExactaEvMode((await r.json()).mode); setSuccess(`Exacta Expected Value visibility set to "${mode}".`); }
      else setError('Failed to update Exacta EV setting');
    } catch (err) { setError('Failed to update Exacta EV setting: ' + err.message); }
    finally { setSavingExactaEv(false); }
  };

  const [calcAlgo, setCalcAlgo] = useState(null);
  const [savingAlgo, setSavingAlgo] = useState(false);
  const fetchCalcAlgo = async () => {
    try {
      const r = await fetch(`${API_URL}/api/quickpick/calc-algorithm`, { credentials: 'include' });
      if (r.ok) setCalcAlgo((await r.json()).value);
    } catch (err) { console.error('Failed to fetch calc algorithm:', err); }
  };
  const updateCalcAlgo = async (value) => {
    setSavingAlgo(true);
    try {
      const r = await fetch(`${API_URL}/api/quickpick/calc-algorithm?value=${value}`, { method: 'PUT', credentials: 'include' });
      if (r.ok) { setCalcAlgo((await r.json()).value); setSuccess(`Pool-score algorithm set to "${value}" (applies to all races immediately).`); }
      else setError('Failed to update algorithm');
    } catch (err) { setError('Failed to update algorithm: ' + err.message); }
    finally { setSavingAlgo(false); }
  };

  useEffect(() => {
    fetchWidgetSettings();
    fetchCalcAlgo();
    fetchExactaEvSetting();
  }, []);
  useEffect(() => { if (activeTab === 'gentix' || activeTab === 'jobs' || activeTab === 'env') fetchCalcAlgo(); }, [activeTab]);

  // Global announcement message (Env tab)
  const [globalMsg, setGlobalMsg] = useState({ message: '', severity: 'info', active: true });
  const [savingGlobalMsg, setSavingGlobalMsg] = useState(false);
  const fetchGlobalMsg = async () => {
    try {
      const r = await fetch(`${API_URL}/api/quickpick/global-message`);
      if (r.ok) {
        const d = await r.json();
        setGlobalMsg({ message: d.message || '', severity: d.severity || 'info', active: d.active !== false });
      }
    } catch (err) { console.error('Failed to fetch global message:', err); }
  };
  const saveGlobalMsg = async () => {
    setSavingGlobalMsg(true);
    try {
      const p = new URLSearchParams({
        message: globalMsg.message || '',
        severity: globalMsg.severity || 'info',
        active: String(!!globalMsg.active),
      });
      const r = await fetch(`${API_URL}/api/quickpick/global-message?${p.toString()}`, { method: 'PUT', credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setGlobalMsg({ message: d.message || '', severity: d.severity || 'info', active: d.active !== false });
        setSuccess(d.show ? 'Global message published to all users.' : 'Global message saved (hidden — empty or inactive).');
      } else setError('Failed to save global message');
    } catch (err) { setError('Failed to save global message: ' + err.message); }
    finally { setSavingGlobalMsg(false); }
  };
  useEffect(() => { if (activeTab === 'env') fetchGlobalMsg(); }, [activeTab]);

  const updateWidgetSetting = async (key, value) => {
    if (!widgetSettings) return;
    const next = {
      showLateSignal: widgetSettings.showLateSignal,
      showStraightBets: widgetSettings.showStraightBets,
      [key]: value,
    };
    setSavingWidget(true);
    try {
      const response = await fetch(
        `${API_URL}/api/quickpick/widget-settings?showLateSignal=${next.showLateSignal}&showStraightBets=${next.showStraightBets}`,
        { method: 'PUT', credentials: 'include' }
      );
      if (response.ok) {
        await fetchWidgetSettings();
        setSuccess('Widget feature updated (visible in all embedded widgets).');
      } else {
        setError('Failed to update widget feature');
      }
    } catch (err) {
      setError('Failed to update widget feature: ' + err.message);
    } finally {
      setSavingWidget(false);
    }
  };

  // --- Ticket consolidation thresholds (per pool) ---
  const [consolThresholds, setConsolThresholds] = useState({ EX: 0, TRI: 0, SPR: 0, DBL: 0, P3: 0, P4: 0, P5: 0, P6: 0 });
  const [consolDraft, setConsolDraft] = useState({ EX: 0, TRI: 0, SPR: 0, DBL: 0, P3: 0, P4: 0, P5: 0, P6: 0 });
  const [savingConsol, setSavingConsol] = useState(false);

  const fetchConsolThresholds = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quickpick/consolidation-settings`);
      if (response.ok) {
        const data = await response.json();
        if (data.thresholds) { setConsolThresholds(data.thresholds); setConsolDraft(data.thresholds); }
      }
    } catch (err) {
      console.error('Failed to fetch consolidation settings:', err);
    }
  };

  useEffect(() => { fetchConsolThresholds(); }, []);

  const saveConsolThresholds = async () => {
    setSavingConsol(true);
    try {
      const pools = ['EX', 'TRI', 'SPR', 'DBL', 'P3', 'P4', 'P5', 'P6'];
      const q = pools.map((p) => `${p}=${Number(consolDraft[p]) || 0}`).join('&');
      const response = await fetch(`${API_URL}/api/quickpick/consolidation-settings?${q}`, { method: 'PUT', credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setConsolThresholds(data.thresholds);
        setSuccess('Consolidation thresholds saved.');
      } else {
        setError('Failed to save consolidation thresholds');
      }
    } catch (err) {
      setError('Failed to save consolidation thresholds: ' + err.message);
    } finally {
      setSavingConsol(false);
    }
  };

  
  // Fetch admin stats
  const fetchAdminStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/quickpick/admin-stats?start_date=${statsStartDate}&end_date=${statsEndDate}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [statsStartDate, statsEndDate]);
  
  // Fetch stats on mount and when dates change
  useEffect(() => {
    fetchAdminStats();
  }, [fetchAdminStats]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Open edit modal
  const openEditModal = (targetUser, e) => {
    e.stopPropagation();
    setEditingUser(targetUser);
    setEditStatus(targetUser.subscription_status || 'none');
    setEditExpiryDate(targetUser.subscription_ends_at ? targetUser.subscription_ends_at.split('T')[0] : '');
    setEditTrialDate(targetUser.trial_ends_at ? targetUser.trial_ends_at.split('T')[0] : '');
    setShowEditModal(true);
  };

  // Handle update user
  // Grant/revoke BetMaker access for a user
  const toggleBetmaker = async (u, e) => {
    if (e) e.stopPropagation();
    setError('');
    setSuccess('');
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${u.id}/betmaker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ enabled: !u.betmaker_enabled })
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update BetMaker access');
      setSuccess(data.message);
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  // Grant/revoke Exacta Value access for a user
  const toggleExactaValue = async (u, e) => {
    if (e) e.stopPropagation();
    setError('');
    setSuccess('');
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${u.id}/exacta-value`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ enabled: !u.exacta_value_enabled })
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to update Exacta Value access');
      setSuccess(data.message);
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            subscription_status: editStatus,
            subscription_ends_at: editExpiryDate ? new Date(editExpiryDate).toISOString() : null,
            trial_ends_at: editTrialDate ? new Date(editTrialDate).toISOString() : null
          })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update user');
      }
      
      setSuccess(`User ${editingUser.email} updated`);
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  // Force password reset for user
  const handleForcePasswordReset = async () => {
    if (!editingUser) return;
    
    if (!window.confirm(`Force ${editingUser.email} to change their password on next login?`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    setResetPasswordLoading(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/reset-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to enable password reset');
      }
      
      setSuccess(`${editingUser.email} will be prompted to change password on next login.`);
      // Update local state
      setEditingUser({ ...editingUser, force_password_reset: true });
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Clear password reset flag
  const handleClearPasswordReset = async () => {
    if (!editingUser) return;
    
    setError('');
    setSuccess('');
    setResetPasswordLoading(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/clear-password-reset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to clear password reset');
      }
      
      setSuccess(`Password reset cleared for ${editingUser.email}.`);
      // Update local state
      setEditingUser({ ...editingUser, force_password_reset: false });
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // Send a password reset email (admin-initiated) — independent of the force-reset flag
  const [sendResetEmailLoading, setSendResetEmailLoading] = useState(false);
  const [tempPasswordInput, setTempPasswordInput] = useState('');
  const [tempPasswordLoading, setTempPasswordLoading] = useState(false);
  const [tempPasswordResult, setTempPasswordResult] = useState('');
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);
  const handleSetTempPassword = async () => {
    if (!editingUser) return;
    setError('');
    setSuccess('');
    setTempPasswordResult('');
    setTempPasswordLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/set-temp-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ password: tempPasswordInput.trim() || null })
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to set temporary password');
      }
      setTempPasswordResult(data.temp_password);
      setSuccess(data.message);
      setTempPasswordInput('');
    } catch (err) {
      setError(err.message);
    } finally {
      setTempPasswordLoading(false);
    }
  };
  const handleSendResetEmail = async () => {
    if (!editingUser) return;
    setError('');
    setSuccess('');
    setSendResetEmailLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/send-reset-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }
      setSuccess(data.message || `Password reset email sent to ${editingUser.email}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSendResetEmailLoading(false);
    }
  };


  // Delete user
  const handleDeleteUser = async (targetUser) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE ${targetUser.email}? This cannot be undone.`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/users/${targetUser.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Invalid server response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete user');
      }
      
      setSuccess(`User ${targetUser.email} deleted`);
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  // Affiliate management
  const openAffiliateModal = (targetUser, action, e) => {
    e.stopPropagation();
    setSelectedUser(targetUser);
    setAffiliateAction(action);
    setSelectedAffid(action === 'assign' ? (targetUser.referred_by || '') : (targetUser.is_affiliate || ''));
    setShowAffiliateModal(true);
  };

  const handleAffiliateAction = async () => {
    if (!selectedUser) return;
    
    setError('');
    setSuccess('');
    
    try {
      let endpoint, body;
      
      if (affiliateAction === 'assign') {
        endpoint = `${API_URL}/api/auth/admin/users/${selectedUser.id}/assign-affiliate`;
        body = { affid: selectedAffid };
      } else {
        endpoint = `${API_URL}/api/auth/admin/users/${selectedUser.id}/make-affiliate`;
        body = { affid: selectedAffid };
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update affiliate');
      }
      
      setSuccess(data.message);
      setShowAffiliateModal(false);
      setSelectedUser(null);
      setSelectedAffid('');
      fetchUsers(search);
    } catch (err) {
      setError(err.message);
    }
  };

  // Remove affiliate status from within the user detail modal (moved here from the
  // main row to avoid accidental mis-clicks). Updates the open modal + the table.
  const removeAffiliateFromDetail = async () => {
    if (!detailUser) return;
    if (!window.confirm(`Remove affiliate status from ${detailUser.email}?`)) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${detailUser.id}/remove-affiliate-link`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to remove affiliate status');
      setSuccess(data.message);
      setDetailUser({ ...detailUser, is_affiliate: null });
      fetchUsers(search);
      fetchAffiliates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Remove affiliate status from within the Edit User Access modal.
  const removeAffiliateFromEdit = async () => {
    if (!editingUser) return;
    if (!window.confirm(`Remove affiliate status from ${editingUser.email}?`)) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${editingUser.id}/remove-affiliate-link`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to remove affiliate status');
      setSuccess(data.message);
      setEditingUser({ ...editingUser, is_affiliate: null });
      fetchUsers(search);
      fetchAffiliates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Create affiliate
  const handleCreateAffiliate = async () => {
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/affiliates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...newAffiliate, contact_email: newAffiliate.contact_email || null })
      });
      
      // Clone response before reading body to allow re-reading if needed
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Invalid server response');
      }
      
      if (!response.ok) {
        throw new Error(formatApiError(data.detail, 'Failed to create affiliate'));
      }
      
      setSuccess(`Affiliate ${newAffiliate.affid} created`);
      setShowCreateAffiliateModal(false);
      setNewAffiliate({ affid: '', name: '', commission_pct: 10, contact_email: '' });
      fetchAffiliates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Download the marketing HTML email with this affiliate's tracked link embedded
  const handleDownloadAffiliateEmail = async (aff) => {
    setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/api/auth/affiliates/${aff.affid}/email-html`, {
        credentials: 'include',
      });
      if (!response.ok) {
        let msg = 'Failed to generate email';
        try { const d = await response.json(); msg = formatApiError(d.detail, msg); } catch {}
        throw new Error(msg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intellitix-affiliate-${aff.affid}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(`Downloaded HTML email for ${aff.affid}`);
    } catch (err) {
      setError(err.message);
    }
  };

  // Edit affiliate
  const openEditAffiliate = (aff) => {
    setEditAffiliate({ affid: aff.affid, name: aff.name || '', commission_pct: aff.commission_pct ?? 0, contact_email: aff.contact_email || '' });
    setShowEditAffiliateModal(true);
    setError(''); setSuccess('');
  };

  const openAffiliateSummary = async (aff) => {
    setShowAffSummary(true);
    setAffSummary(null);
    setLoadingAffSummary(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/admin/affiliates/${aff.affid}/summary`, { credentials: 'include' });
      if (r.ok) setAffSummary(await r.json());
    } catch (err) {
      console.error('Failed to fetch affiliate summary:', err);
    } finally {
      setLoadingAffSummary(false);
    }
  };

  const exportAffiliateSummaryCsv = () => {
    if (!affSummary) return;
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const a = affSummary.affiliate;
    const s = affSummary.stats;
    const lines = [];
    lines.push(['Affiliate Summary', a.affid, a.name || ''].map(esc).join(','));
    lines.push(['Commission Rate', `${a.commission_pct}%`].map(esc).join(','));
    lines.push(['Generated', new Date().toISOString()].map(esc).join(','));
    lines.push('');
    lines.push(['Signups', 'Paying Customers', 'Revenue', 'Commission'].join(','));
    lines.push([s.total_signups, s.paying_customers, s.estimated_revenue, s.estimated_commission].map(esc).join(','));
    lines.push('');
    lines.push(['Email', 'Name', 'Status', 'Charges', 'Revenue', 'Commission', 'Joined'].join(','));
    affSummary.users.forEach((u) => {
      lines.push([u.email, u.name || '', u.subscription_status || '', u.charges || 0, (u.revenue || 0).toFixed(2), (u.commission || 0).toFixed(2), u.created_at ? new Date(u.created_at).toISOString().split('T')[0] : ''].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `affiliate_${a.affid}_summary_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download ALL user accounts as CSV (admin dump for email campaigns etc.)
  const [exportingUsers, setExportingUsers] = useState(false);
  const downloadUsersCsv = async () => {
    setError(''); setSuccess('');
    setExportingUsers(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/users/export`, { credentials: 'include' });
      if (!response.ok) {
        let msg = 'Failed to export users';
        try { const d = await response.json(); msg = formatApiError(d.detail, msg); } catch {}
        throw new Error(msg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intellitix_users_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Downloaded all user accounts (CSV)');
    } catch (err) {
      setError(err.message);
    } finally {
      setExportingUsers(false);
    }
  };




  const handleUpdateAffiliate = async () => {
    setError(''); setSuccess('');
    try {
      const response = await fetch(`${API_URL}/api/auth/affiliates/${editAffiliate.affid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editAffiliate.name,
          commission_pct: editAffiliate.commission_pct,
          contact_email: editAffiliate.contact_email || null,
        }),
      });
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(text || 'Invalid server response'); }
      if (!response.ok) throw new Error(formatApiError(data.detail, 'Failed to update affiliate'));
      setSuccess(`Affiliate ${editAffiliate.affid} updated`);
      setShowEditAffiliateModal(false);
      fetchAffiliates();
    } catch (err) {
      setError(err.message);
    }
  };

  // User detail panel
  const openUserDetail = async (targetUser) => {
    setDetailUser(targetUser);
    setShowUserDetail(true);
    setLoadingActivity(true);
    setLoadingLoginEvents(true);
    setLoginEvents([]);

    try {
      const response = await fetch(
        `${API_URL}/api/auth/admin/users/${targetUser.id}/activity`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUserActivity(data.activity || []);
      }
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoadingActivity(false);
    }

    try {
      const r = await fetch(
        `${API_URL}/api/auth/admin/users/${targetUser.id}/login-events`,
        { credentials: 'include' }
      );
      if (r.ok) {
        const d = await r.json();
        setLoginEvents(d.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch login events:', err);
    } finally {
      setLoadingLoginEvents(false);
    }

    setLoadingTxns(true);
    setTxns(null);
    try {
      const r = await fetch(
        `${API_URL}/api/auth/admin/users/${targetUser.id}/transactions`,
        { credentials: 'include' }
      );
      if (r.ok) setTxns(await r.json());
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoadingTxns(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
      trial: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      cancelled: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      expired: 'bg-red-500/20 text-red-400 border-red-500/50',
      none: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
    };
    return badges[status] || badges.none;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-2xl font-bold text-white">
              IntelliTix<span className="text-emerald-400">®</span>
            </Link>
            <span className="text-slate-500">|</span>
            <span className="text-slate-400 font-medium">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-slate-400 hover:text-white transition">
              Back to App
            </Link>
            <button 
              onClick={logout}
              className="text-sm text-slate-400 hover:text-white transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'users' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('affiliates')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'affiliates' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Affiliates
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'stats' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Stats
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'jobs' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Jobs
          </button>
          <button
            onClick={() => setActiveTab('env')}
            data-testid="tab-env"
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'env'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Env
          </button>
          <button
            onClick={() => setActiveTab('downloads')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'downloads' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Downloads
          </button>
          <button
            onClick={() => setActiveTab('gentix')}
            data-testid="tab-gentix"
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'gentix'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Generated Tix
          </button>
          <button
            onClick={() => setActiveTab('signals')}
            data-testid="tab-signals"
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'signals'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Signal Effects
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'pricing' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-pricing"
          >
            Pricing
          </button>
          <button
            onClick={() => setActiveTab('feed')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'feed'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-feed"
          >
            Feed
          </button>
          <button
            onClick={() => setActiveTab('tracks')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'tracks'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-tracks"
          >
            Tracks
          </button>
          <button
            onClick={() => setActiveTab('liveops')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'liveops'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-liveops"
          >
            Live Ops
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'clients'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-clients"
          >
            Widget Clients
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'messages'
                ? 'bg-emerald-600 text-white'
                : unreadMessages > 0
                  ? 'bg-orange-500 text-white hover:bg-orange-400'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="admin-tab-messages"
          >
            Messages
            {unreadMessages > 0 && activeTab !== 'messages' && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white text-orange-600 text-xs font-bold"
                data-testid="admin-messages-unread-count"
              >
                {unreadMessages}
              </span>
            )}
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">User Management</h1>
                <p className="text-slate-400 text-sm">Click on a row to see user details. Click Edit to modify access.</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={downloadUsersCsv}
                  disabled={exportingUsers}
                  className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                  data-testid="admin-download-users-csv"
                  title="Download every user account as a CSV"
                >
                  {exportingUsers ? 'Exporting…' : 'Download Users CSV'}
                </button>
                <span className="text-slate-400 text-sm">{filteredUsers.length} users</span>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or name..."
                className="w-64 px-3 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="inactive">Inactive</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
                <option value="none">None</option>
              </select>
              <select
                value={affiliateFilter}
                onChange={(e) => setAffiliateFilter(e.target.value)}
                data-testid="affiliate-filter"
                className="px-3 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Affiliates</option>
                {affiliates.map((a) => (
                  <option key={a.affid} value={a.affid}>{a.affid}{a.name ? ` — ${a.name}` : ''}</option>
                ))}
              </select>
              {(search || statusFilter !== 'all' || affiliateFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setStatusFilter('all'); setAffiliateFilter('all'); }}
                  className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Users Table */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th onClick={() => toggleSort('email')} data-testid="sort-email" className="text-left px-4 py-3 text-sm font-semibold text-slate-300 cursor-pointer select-none hover:text-white">Email{sortArrow('email')}</th>
                      <th onClick={() => toggleSort('name')} data-testid="sort-name" className="text-left px-4 py-3 text-sm font-semibold text-slate-300 cursor-pointer select-none hover:text-white">Name{sortArrow('name')}</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Expires</th>
                      <th onClick={() => toggleSort('last_login')} data-testid="sort-last-login" className="text-left px-4 py-3 text-sm font-semibold text-slate-300 cursor-pointer select-none hover:text-white">Last Login{sortArrow('last_login')}</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Downloads</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Referred By</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Is Affiliate</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">BetMaker</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Sharing</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {loading ? (
                      <tr>
                        <td colSpan="11" className="px-4 py-8 text-center text-slate-400">
                          Loading...
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="px-4 py-8 text-center text-slate-400">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <tr 
                          key={u.id} 
                          onClick={() => openUserDetail(u)}
                          className="hover:bg-slate-800/50 cursor-pointer"
                        >
                          <td className="px-4 py-3 text-white">{u.email}</td>
                          <td className="px-4 py-3 text-slate-300">{u.name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusBadge(u.subscription_status)}`}>
                              {u.subscription_status || 'none'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm">{formatDate(u.subscription_ends_at)}</td>
                          <td className="px-4 py-3 text-slate-400 text-sm" title={u.last_login ? formatDateTime(u.last_login) : 'Never logged in'}>{formatDate(u.last_login)}</td>
                          <td className="px-4 py-3 text-slate-300 text-sm font-medium">{u.download_count || 0}</td>
                          <td className="px-4 py-3">
                            {u.referred_by ? (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-purple-500/20 text-purple-400 border border-purple-500/50">
                                {u.referred_by}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => openAffiliateModal(u, 'assign', e)}
                                className="text-xs text-slate-500 hover:text-purple-400 transition"
                              >
                                + Assign
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {u.is_affiliate ? (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/50">
                                {u.is_affiliate}
                              </span>
                            ) : (
                              <button
                                onClick={(e) => openAffiliateModal(u, 'link', e)}
                                className="text-xs text-slate-500 hover:text-amber-400 transition"
                              >
                                + Link
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {u.role === 'admin' ? (
                              <span className="text-xs text-slate-500">Always</span>
                            ) : u.betmaker_enabled ? (
                              <button
                                onClick={(e) => toggleBetmaker(u, e)}
                                data-testid={`betmaker-toggle-${u.id}`}
                                className="px-2 py-1 text-xs font-medium rounded border transition bg-amber-500/20 text-amber-400 border-amber-500/50 hover:bg-amber-500/30"
                                title="Click to revoke BetMaker access"
                              >
                                On
                              </button>
                            ) : u.subscription_status === 'trial' ? (
                              <button
                                onClick={(e) => toggleBetmaker(u, e)}
                                data-testid={`betmaker-toggle-${u.id}`}
                                className="px-2 py-1 text-xs font-medium rounded border transition bg-sky-500/20 text-sky-300 border-sky-500/50 hover:bg-sky-500/30"
                                title="Included free during trial. Click to grant a permanent BetMaker flag."
                              >
                                Trial
                              </button>
                            ) : (
                              <button
                                onClick={(e) => toggleBetmaker(u, e)}
                                data-testid={`betmaker-toggle-${u.id}`}
                                className="px-2 py-1 text-xs font-medium rounded border transition bg-slate-700/40 text-slate-400 border-slate-600 hover:bg-slate-700"
                                title="Click to grant BetMaker access"
                              >
                                Off
                              </button>
                            )}
                            <div className="mt-1">
                              {u.role === 'admin' ? (
                                <span className="text-[10px] text-slate-600">EV: Always</span>
                              ) : (
                                <button
                                  onClick={(e) => toggleExactaValue(u, e)}
                                  data-testid={`exacta-toggle-${u.id}`}
                                  className={`px-2 py-0.5 text-[10px] font-medium rounded border transition ${u.exacta_value_enabled ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50 hover:bg-emerald-500/30' : 'bg-slate-700/40 text-slate-400 border-slate-600 hover:bg-slate-700'}`}
                                  title="Toggle Exacta Value access"
                                >
                                  EV {u.exacta_value_enabled ? 'On' : 'Off'}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {u.shared_flag ? (
                              <span
                                data-testid={`shared-flag-${u.id}`}
                                className="px-2 py-1 text-xs font-medium rounded border bg-red-500/20 text-red-400 border-red-500/50"
                                title={`${u.distinct_ips} distinct IPs in last 5 days — possible account sharing. Click row to review.`}
                              >
                                ⚠ Shared? ({u.distinct_ips})
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">{u.distinct_ips ? `${u.distinct_ips} IP${u.distinct_ips > 1 ? 's' : ''}` : '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {u.role === 'admin' ? (
                              <span className="text-slate-500 text-sm">Admin</span>
                            ) : (
                              <button
                                onClick={(e) => openEditModal(u, e)}
                                className="px-3 py-1 text-sm bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded transition"
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Affiliate Management</h1>
                <p className="text-slate-400 text-sm">Create and manage affiliate accounts</p>
              </div>
              <button
                onClick={() => setShowCreateAffiliateModal(true)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition"
              >
                + Create Affiliate
              </button>
            </div>

            {/* Filters */}
            <div className="flex gap-3 mb-4 items-center">
              <input
                type="text"
                value={affiliateSearch}
                onChange={(e) => setAffiliateSearch(e.target.value)}
                placeholder="Search ID, name, or email..."
                className="w-64 px-3 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {affiliateSearch && (
                <button
                  type="button"
                  onClick={() => setAffiliateSearch('')}
                  className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                >
                  Clear
                </button>
              )}
              <span className="text-slate-400 text-sm ml-auto">{filteredAffiliates.length} affiliates</span>
            </div>

            {/* Affiliates Table */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Affiliate ID</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Name</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Commission %</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Contact Email</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Signups</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Conversions</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Created</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {filteredAffiliates.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                          {affiliates.length === 0 ? "No affiliates yet. Click \"Create Affiliate\" to add one." : "No affiliates match your search."}
                        </td>
                      </tr>
                    ) : (
                      filteredAffiliates.map((aff) => (
                        <tr key={aff.affid} className="hover:bg-slate-800/50">
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-sm font-mono font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/50">
                              {aff.affid}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-white">{aff.name}</td>
                          <td className="px-4 py-3 text-emerald-400 font-medium">{aff.commission_pct}%</td>
                          <td className="px-4 py-3 text-slate-300">{aff.contact_email || '-'}</td>
                          <td className="px-4 py-3 text-white font-medium">{aff.signups || 0}</td>
                          <td className="px-4 py-3 text-emerald-400 font-medium">{aff.conversions || 0}</td>
                          <td className="px-4 py-3 text-slate-400 text-sm">{formatDate(aff.created_at)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openAffiliateSummary(aff)}
                              data-testid={`affiliate-summary-${aff.affid}`}
                              className="px-3 py-1 mr-2 text-sm font-medium rounded border border-emerald-600/60 text-emerald-400 hover:bg-emerald-600/20 transition-colors">
                              Summary
                            </button>
                            <button
                              onClick={() => handleDownloadAffiliateEmail(aff)}
                              data-testid={`download-affiliate-email-${aff.affid}`}
                              title="Download marketing HTML email with this affiliate's tracked link"
                              className="px-3 py-1 mr-2 text-sm font-medium rounded border border-sky-600/60 text-sky-400 hover:bg-sky-600/20 transition-colors">
                              Download HTML Email
                            </button>
                            <button
                              onClick={() => openEditAffiliate(aff)}
                              data-testid={`edit-affiliate-${aff.affid}`}
                              className="px-3 py-1 text-sm font-medium rounded border border-slate-600 text-slate-200 hover:bg-slate-700 hover:border-slate-500 transition-colors">
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Activity Stats</h1>
                <p className="text-slate-400 text-sm">Logins and downloads across the platform</p>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Date Range</h2>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={statsStartDate}
                    onChange={(e) => setStatsStartDate(e.target.value)}
                    className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <span className="text-slate-500">to</span>
                  <input
                    type="date"
                    value={statsEndDate}
                    onChange={(e) => setStatsEndDate(e.target.value)}
                    className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    onClick={fetchAdminStats}
                    disabled={statsLoading}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white text-sm font-medium rounded-lg transition"
                  >
                    {statsLoading ? '...' : 'Apply'}
                  </button>
                </div>
              </div>
              
              {/* Totals */}
              {adminStats && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-slate-800 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-emerald-400">{adminStats.total_logins}</div>
                      <div className="text-slate-400 text-sm">Total Logins</div>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-amber-400">{adminStats.total_downloads}</div>
                      <div className="text-slate-400 text-sm">Total Downloads</div>
                    </div>
                  </div>
                  
                  {/* Daily breakdown table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-2 px-3 text-slate-400 font-medium text-sm">Date</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium text-sm">Logins</th>
                          <th className="text-right py-2 px-3 text-slate-400 font-medium text-sm">Downloads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...adminStats.daily]?.reverse().map((day) => (
                          <tr key={day.date} className="border-b border-slate-800 hover:bg-slate-800/50">
                            <td className="py-2 px-3 text-white text-sm">{day.date}</td>
                            <td className="py-2 px-3 text-right text-emerald-400 font-medium text-sm">{day.logins}</td>
                            <td className="py-2 px-3 text-right text-amber-400 font-medium text-sm">{day.downloads}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              
              {statsLoading && (
                <div className="text-center py-4 text-slate-400">Loading stats...</div>
              )}
            </div>
          </>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Background Jobs</h1>
                <p className="text-slate-400 text-sm">Manage scheduled tasks and background processes</p>
              </div>
            </div>

            {/* Auto Tri Bet (Strong-key Trifecta) */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="autotri-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    Auto Tri Bet (Strong-key Trifecta)
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${
                      autotriCfg?.enabled ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                                          : 'bg-red-500/20 text-red-400 border-red-500/50'}`}
                      data-testid="autotri-enabled-badge">
                      {autotriCfg?.enabled ? 'LIVE' : 'OFF'}
                    </span>
                  </h2>
                  <p className="text-slate-400 text-sm">Fires a Strong-key Trifecta at 1 MTP for qualifying races (field 9–11, 1 Strong, 3–4 fades). Posts the <span className="font-mono">IntellitixAutoTri-</span> ticket string to the BLR endpoint. These settings also drive the user "+ROI Trifecta Play" Opt-In.</p>
                </div>
                <button
                  onClick={() => saveAutotri({ enabled: !autotriCfg?.enabled })}
                  disabled={savingAutotri || !autotriCfg}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition ${
                    autotriCfg?.enabled ? 'bg-red-600 hover:bg-red-500 text-white'
                                        : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
                  data-testid="autotri-toggle-btn">
                  {autotriCfg?.enabled ? 'Turn OFF' : 'Turn ON'}
                </button>
              </div>

              {autotriCfg && (
                <div className="space-y-4">
                  <div className="flex gap-6 flex-wrap xl:flex-nowrap">
                  <div className="space-y-4 flex-1 min-w-0">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">Funding version per field size</label>
                    <div className="flex flex-wrap items-center gap-4" data-testid="autotri-versions">
                      <span className="text-sm font-semibold text-slate-400 w-20">Strong:</span>
                      {['9', '10', '11'].map((fs) => (
                        <div key={fs} className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-300 w-8">FS {fs}</span>
                          <select
                            value={autotriCfg.versions?.[fs] || 'V1'}
                            onChange={(e) => {
                              const nv = { ...autotriCfg.versions, [fs]: e.target.value };
                              setAutotriCfg({ ...autotriCfg, versions: nv });
                              saveAutotri({ versions: { [fs]: e.target.value } });
                            }}
                            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
                            data-testid={`autotri-version-${fs}`}>
                            <option value="V1">V1 — Full Coverage</option>
                            <option value="V2">V2 — Exclude Low Prob</option>
                            <option value="V3">V3 — V2 + Buy-Back</option>
                            <option value="off">Off</option>
                          </select>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2" data-testid="autotri-versions-nostrong">
                      <span className="text-sm font-semibold text-slate-400 w-20">Keys Only:</span>
                      {['9', '10', '11'].map((fs) => {
                        const disabled = fs !== '10';   // only FS10 x 4-fades qualifies for no-strong
                        return (
                          <div key={fs} className={`flex items-center gap-2 ${disabled ? 'opacity-40' : ''}`}>
                            <span className="text-sm font-semibold text-slate-300 w-8">FS {fs}</span>
                            <select
                              disabled={disabled}
                              title={disabled ? 'No-Strong plays only qualify at field size 10 (×4 fades)' : undefined}
                              value={autotriCfg.versions_nostrong?.[fs] || 'V1'}
                              onChange={(e) => {
                                const nv = { ...autotriCfg.versions_nostrong, [fs]: e.target.value };
                                setAutotriCfg({ ...autotriCfg, versions_nostrong: nv });
                                saveAutotri({ versions_nostrong: { [fs]: e.target.value } });
                              }}
                              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white disabled:cursor-not-allowed"
                              data-testid={`autotri-version-nostrong-${fs}`}>
                              <option value="V1">V1 — Full Coverage</option>
                              <option value="V2">V2 — Exclude Low Prob</option>
                              <option value="V3">V3 — V2 + Buy-Back</option>
                              <option value="off">Off</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-2" data-testid="autotri-fs8-row">
                      <span className="text-sm font-semibold text-slate-400 w-20">Strong FS8:</span>
                      <select
                        value={autotriCfg.fs8 || '1'}
                        onChange={(e) => { setAutotriCfg({ ...autotriCfg, fs8: e.target.value }); saveAutotri({ fs8: e.target.value }); }}
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
                        data-testid="autotri-fs8-select">
                        <option value="1">1 Fade</option>
                        <option value="12">1 or 2 Fades</option>
                        <option value="off">Off</option>
                      </select>
                      <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                        <input
                          type="checkbox" checked={autotriCfg.fs8_equal ?? true}
                          onChange={(e) => { setAutotriCfg({ ...autotriCfg, fs8_equal: e.target.checked }); saveAutotri({ fs8_equal: e.target.checked }); }}
                          data-testid="autotri-fs8-equal" />
                        Equal weight
                      </label>
                      <span className="text-xs text-slate-500">Always V1 Full Coverage · Strong wins only · others 2nd/3rd · all fades excluded</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1.5"><b>Strong</b> = 1 must-use key (FS 9–11, 3–4 fades). <b>Keys Only</b> = no strong, top-2 consensus keys (FS 10 × 4 fades only; FS 9/11 N/A).</p>
                  </div>

                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Bankroll per play ($)</label>
                      <input
                        type="number" min="1" step="1"
                        value={autotriCfg.bankroll ?? 100}
                        onChange={(e) => setAutotriCfg({ ...autotriCfg, bankroll: e.target.value })}
                        onBlur={(e) => saveAutotri({ bankroll: e.target.value })}
                        className="w-28 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
                        data-testid="autotri-bankroll" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Opt-In exposure (user chiclet)</label>
                      <select
                        value={autotriCfg.opt_in_exposure || 'admin'}
                        onChange={(e) => {
                          setAutotriCfg({ ...autotriCfg, opt_in_exposure: e.target.value });
                          saveAutotri({ opt_in_exposure: e.target.value });
                        }}
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
                        data-testid="autotri-exposure">
                        <option value="admin">Admin only</option>
                        <option value="admin_affiliate">Admin + Affiliates</option>
                        <option value="all">All users</option>
                      </select>
                    </div>
                  </div>
                  </div>
                  <AlertEmailsBox emails={autotriCfg.alert_emails || []} saving={savingAutotri} onSave={(alert_emails) => saveAutotri({ alert_emails })} />
                  </div>
                  <p className="text-xs text-slate-600">
                    The <b>job</b> (toggle above) auto-fires and posts to the BLR endpoint. The <b>Opt-In</b> exposure controls who sees the "+ROI Trifecta Play" chiclet in Up Next (IntelliTix & iPro) — opting in builds the play in iPro for manual ADW download; it never posts to BLR. Both use the per-field-size version and bankroll above.
                  </p>

                  {/* Auto-Tri Sent to ASP — audit log (accordion, collapsed by default) */}
                  <div className="border-t border-slate-800 pt-3">
                    <button type="button" onClick={() => setAutotriLogOpen((o) => !o)}
                      className="flex items-center gap-2 w-full text-left" data-testid="autotri-log-toggle">
                      <svg className={`w-4 h-4 text-slate-500 transition-transform ${autotriLogOpen ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                      <span className="text-sm font-semibold text-slate-200">Sent to ASP — audit log</span>
                      <span className="text-xs text-slate-600">(auto-fired tickets — posted to BLRPost or HELD by avoid list)</span>
                    </button>
                    {autotriLogOpen && (
                      <AutoTriLogPanel endpoint="/api/feed/autotri-log" deletable />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* K3 Demotion rule (read-only) */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="k3-rule-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-white">K3 Demotion <span className="text-xs font-normal text-slate-500">(all rule sets)</span></h2>
                  <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                    In a race with <b>exactly 3 keys</b>, the lowest-ranked key is demoted Key → Runner when it's <b>not</b> the 1-MTP favorite AND <b>projOdds / normalized-ML &gt; ratio</b>. Strong (must-use) wins — a Strong key is never demoted. Applies live-forward only; historical stats are never changed.
                  </p>
                </div>
                {k3cfg && (
                  <div className="flex items-center gap-2 shrink-0" data-testid="k3-rule-status">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${k3cfg.enabled ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/50' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                      {k3cfg.enabled ? 'ACTIVE' : 'OFF'}
                    </span>
                  </div>
                )}
              </div>
              {k3cfg && (
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1" data-testid="k3-cutoff">
                    <span className="text-slate-400">Active from </span><span className="font-bold text-white">{k3cfg.cutoff_date || '—'}</span>
                  </span>
                  <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1" data-testid="k3-ratio">
                    <span className="text-slate-400">Ratio &gt; </span><span className="font-bold text-white">{k3cfg.ratio}</span>
                  </span>
                  <span className="rounded bg-slate-800/60 border border-slate-800 px-2 py-1 text-slate-500 italic">read-only · frozen cutoff</span>
                </div>
              )}
            </div>

            {/* Live Sent to ASP Post (admin ADW manual wager send) */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="asp-post-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    Live Sent to ASP Post
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${aspCfg?.enabled ? 'bg-rose-500/20 text-rose-300 border border-rose-500/50' : 'bg-slate-700 text-slate-400'}`} data-testid="asp-post-status">
                      {aspCfg?.enabled ? 'LIVE' : 'OFF'}
                    </span>
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Enables the <b>ADW</b> button in IntelliTix &amp; iPro, which posts real wagers to BLRPost. Visible to an <b>admin OR</b> any approved IP below.</p>
                </div>
                <button
                  onClick={() => saveAsp({ enabled: !aspCfg?.enabled })}
                  disabled={!aspCfg}
                  data-testid="asp-post-toggle"
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${aspCfg?.enabled ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}>
                  {aspCfg?.enabled ? 'Turn OFF' : 'Turn ON'}
                </button>
              </div>
              {aspCfg && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">Approved IPs (button also shows for any admin)</label>
                    <div className="flex flex-wrap gap-2 mb-2" data-testid="asp-ip-list">
                      {(aspCfg.allowed_ips || []).length === 0 && <span className="text-xs text-slate-600">No IPs added yet.</span>}
                      {(aspCfg.allowed_ips || []).map((ip) => (
                        <span key={ip} className="inline-flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1 text-sm text-slate-200" data-testid={`asp-ip-${ip}`}>
                          {ip}
                          <button onClick={() => saveAsp({ allowed_ips: (aspCfg.allowed_ips || []).filter((x) => x !== ip) })}
                            className="text-slate-500 hover:text-rose-400" title="Remove" data-testid={`asp-ip-remove-${ip}`}>✕</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input value={aspNewIp} onChange={(e) => setAspNewIp(e.target.value)} placeholder="e.g. 75.127.228.245"
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white w-56" data-testid="asp-ip-input" />
                      <button
                        onClick={() => { const ip = aspNewIp.trim(); if (ip && !(aspCfg.allowed_ips || []).includes(ip)) { saveAsp({ allowed_ips: [...(aspCfg.allowed_ips || []), ip] }); setAspNewIp(''); } }}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-slate-700 hover:bg-slate-600 text-slate-100" data-testid="asp-ip-add">Add IP</button>
                      {aspIp && (
                        <button
                          onClick={() => { if (!(aspCfg.allowed_ips || []).includes(aspIp)) saveAsp({ allowed_ips: [...(aspCfg.allowed_ips || []), aspIp] }); }}
                          className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-700 hover:bg-emerald-600 text-white" data-testid="asp-ip-add-mine">
                          Add my IP ({aspIp})
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Your detected IP: <span className="text-slate-400 font-mono" data-testid="asp-your-ip">{aspIp || 'unknown'}</span></p>
                  </div>

                  <div className="border-t border-slate-800 pt-3">
                    <button type="button" onClick={() => setAspLogOpen((o) => !o)}
                      className="flex items-center gap-2 w-full text-left" data-testid="asp-log-toggle">
                      <svg className={`w-4 h-4 text-slate-500 transition-transform ${aspLogOpen ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                      <span className="text-sm font-semibold text-slate-200">Recent sends (audit log)</span>
                      <span className="text-xs text-slate-600">(manual ADW wagers posted to BLRPost)</span>
                    </button>
                    {aspLogOpen && (
                      <div className="mt-3" data-testid="asp-log-panel">
                        <div className="flex items-center justify-end gap-2 text-xs text-slate-400 mb-2" data-testid="asp-log-range">
                          <span>From</span>
                          <input type="date" value={aspFromD} max={aspToD} onChange={(e) => setAspFromD(e.target.value)}
                            className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-slate-200" data-testid="asp-log-from" />
                          <span>To</span>
                          <input type="date" value={aspToD} min={aspFromD} onChange={(e) => setAspToD(e.target.value)}
                            className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-slate-200" data-testid="asp-log-to" />
                          <button onClick={() => { setAspFrom(aspFromD); setAspTo(aspToD); fetchAspLog(aspFromD, aspToD); }}
                            className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors"
                            data-testid="asp-log-apply">Apply</button>
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-slate-800" data-testid="asp-audit-log">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-800/60 text-slate-400">
                              <tr><th className="text-left px-2 py-1.5">When (ET)</th><th className="text-left px-2 py-1.5">Track/Race</th><th className="text-left px-2 py-1.5">Files</th><th className="text-left px-2 py-1.5">Fired by</th><th className="text-left px-2 py-1.5">Result</th><th className="text-right px-2 py-1.5">ROI</th></tr>
                            </thead>
                            <tbody>
                              {(aspRecent || []).length === 0 && (
                                <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-600">No sends in this date range.</td></tr>
                              )}
                              {(aspRecent || []).map((r, i) => (
                                <tr key={i} className="border-t border-slate-800 text-slate-300" data-testid={`asp-audit-row-${i}`}>
                                  <td className="px-2 py-1.5 whitespace-nowrap">{r.ts ? new Date(r.ts).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—'}</td>
                                  <td className="px-2 py-1.5 whitespace-nowrap">{r.track} R{r.race}</td>
                                  <td className="px-2 py-1.5">{(r.files || []).map((f) => `${f.label} (${f.combos})`).join(' · ')}</td>
                                  <td className="px-2 py-1.5">{r.fired_by || '—'}</td>
                                  <td className="px-2 py-1.5">{(r.files || []).map((f, j) => (
                                    <span key={j} className={f.ok ? 'text-emerald-400' : 'text-rose-400'}>{f.ok ? `✓ ${f.status_code}` : `⚠ ${f.error || f.status_code}`}{j < (r.files.length - 1) ? ' · ' : ''}</span>
                                  ))}</td>
                                  <RoiCell grade={r.grade} legacy={r.legacy} testId={`asp-audit-roi-${i}`} />
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* CSR Report upload */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="csr-card">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">CSR Report</h2>
                  <p className="text-slate-400 text-sm">Upload <span className="font-mono">CSR_YYYY-MM-DD.csv</span> (a few times/day for scratches). Each upload replaces that race-day's data.</p>
                  <div className="mt-2 text-sm text-slate-400" data-testid="csr-status-row">
                    {csrStatus?.last_upload_at ? (
                      <>Last: <span className="text-slate-200">{new Date(csrStatus.last_upload_at).toLocaleString()}</span> · {csrStatus.last_rows} rows · days {(csrStatus.last_days || []).join(', ')} · {(csrStatus.last_tracks || []).length} tracks · cached {csrStatus.cached_rows}</>
                    ) : <span className="text-slate-500">No CSR uploaded yet.</span>}
                  </div>
                </div>
                <label className={`px-3 py-1.5 text-sm rounded-lg cursor-pointer transition ${uploadingCsr ? 'bg-slate-700 text-slate-400' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`} data-testid="csr-upload-label">
                  {uploadingCsr ? 'Uploading…' : 'Upload CSR CSV'}
                  <input type="file" accept=".csv" className="hidden"
                    onChange={(e) => { uploadCsr(e.target.files?.[0]); e.target.value = ''; }}
                    disabled={uploadingCsr} data-testid="csr-upload-input" />
                </label>
              </div>
            </div>

            {/* Feed Worker health (cross-replica lock + enabled + last run) */}
            {(() => {
              const fw = feedWorker || {};
              const lock = fw.worker_lock || {};
              const last = fw.last_run || {};
              return (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="feed-worker-card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        Feed Worker (FTP Ingest)
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded border ${
                            fw.enabled
                              ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                              : 'bg-slate-600/30 text-slate-400 border-slate-600/50'
                          }`}
                          data-testid="feed-worker-enabled"
                        >
                          {fw.enabled ? 'ENABLED' : 'PAUSED'}
                        </span>
                      </h2>
                      <p className="text-slate-400 text-sm">
                        Polls the FTP feed every {fw.interval_seconds ?? '—'}s. In production 2 replicas run; the lock guarantees only one fetches at a time.
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid="feed-worker-lock-row">
                        <span className="text-slate-500">Worker lock:</span>
                        {lock.active ? (
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                              lock.held_by_self
                                ? 'bg-emerald-900/60 text-emerald-300 border-emerald-700/50'
                                : 'bg-amber-900/50 text-amber-300 border-amber-700/50'
                            }`}
                            data-testid="feed-worker-lock-badge"
                          >
                            {lock.held_by_self ? 'HELD BY THIS REPLICA' : `HELD BY REPLICA ${lock.holder}`}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-slate-800 text-slate-400 border-slate-700" data-testid="feed-worker-lock-badge">
                            IDLE (free)
                          </span>
                        )}
                        <span className="text-slate-500">· this replica: <span className="text-slate-300 font-mono">{lock.this_replica || '—'}</span></span>
                        {lock.active && lock.expires_at && (
                          <span className="text-slate-500">· lock expires {new Date(lock.expires_at).toLocaleTimeString()}</span>
                        )}
                      </div>
                      {last.last_run && (
                        <p className="text-slate-500 text-xs mt-1">
                          Last run: {new Date(last.last_run).toLocaleString()} · {last.run_type || '—'}
                          {last.summary && <> · {last.summary.processed || 0} processed, {last.summary.error || 0} errors</>}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={fetchFeedWorker}
                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition"
                      data-testid="feed-worker-refresh-btn"
                    >
                      {loadingFeedWorker ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Scratch Sweep Job (Equibase late-changes) */}
            {(() => {
              const s = scratchStatus || {};
              return (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="scratch-sweep-card">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        Scratch Sweep (Equibase Late Changes)
                        <span className="px-2 py-0.5 text-xs font-semibold rounded border bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
                          HOURLY
                        </span>
                      </h2>
                      <p className="text-slate-400 text-sm">
                        Pulls early scratches across the whole card from Equibase RSS, hourly 9 AM–9 PM ET. The live cycle for the next race overrides these; official races are never touched.
                      </p>
                      {s.last_run ? (
                        <p className="text-slate-500 text-xs mt-1" data-testid="scratch-last-run">
                          Last run: <span className="text-slate-300">{new Date(s.last_run).toLocaleString()}</span> · {s.run_type || 'Auto'}
                          {' · '}date {s.date || '—'} · {s.tracks ?? 0} tracks · {s.scratches_found ?? 0} scratches found · {s.entries_updated ?? 0} entries updated
                        </p>
                      ) : (
                        <p className="text-slate-500 text-xs mt-1" data-testid="scratch-last-run">
                          {loadingScratch ? 'Loading…' : 'No run recorded yet.'}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={fetchScratchStatus}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition"
                        data-testid="scratch-refresh-btn"
                      >
                        {loadingScratch ? 'Refreshing…' : 'Refresh'}
                      </button>
                      <button
                        onClick={runScratchNow}
                        disabled={runningScratch}
                        className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition"
                        data-testid="scratch-run-now-btn"
                      >
                        {runningScratch ? 'Running…' : 'Run Now'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Recompute prior dates (math + backtests) */}
            {(() => {
              const s = rcStatus || {};
              const running = s.state === 'running';
              const pct = s.total_races ? Math.round((s.done_races || 0) / s.total_races * 100) : 0;
              return (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="recompute-card">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        Recompute Date Range
                        <span className="px-2 py-0.5 text-xs font-semibold rounded border bg-amber-500/20 text-amber-400 border-amber-500/50">
                          MANUAL
                        </span>
                      </h2>
                      <p className="text-slate-400 text-sm max-w-2xl">
                        Re-runs the will-pay-final math (imputed odds, projected odds, scores, norm) for every stored race in the range, then re-runs Top Performers + Generated Tix so their stats reflect the new values. Frozen Strong/Fade signals are left as originally shown. Limit 31 days.
                      </p>
                    </div>
                    <div className="flex items-end gap-2 shrink-0">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Start</label>
                        <input
                          type="date" value={rcStart} onChange={(e) => setRcStart(e.target.value)}
                          className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-2 py-1.5"
                          data-testid="recompute-start-date"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">End</label>
                        <input
                          type="date" value={rcEnd} onChange={(e) => setRcEnd(e.target.value)}
                          className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-2 py-1.5"
                          data-testid="recompute-end-date"
                        />
                      </div>
                      <button
                        onClick={startRecompute}
                        disabled={rcStarting || running}
                        className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition"
                        data-testid="recompute-run-btn"
                      >
                        {running ? 'Running…' : rcStarting ? 'Starting…' : 'Recompute'}
                      </button>
                    </div>
                  </div>

                  {(running || s.state === 'done' || s.state === 'error') && (
                    <div className="mt-3 border-t border-slate-800 pt-3" data-testid="recompute-progress">
                      {running && (
                        <div>
                          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                            <span>
                              Phase: <span className="text-sky-400 font-medium capitalize">{s.phase || 'starting'}</span>
                              {s.current_date && <span className="text-slate-500"> · {s.current_date}</span>}
                            </span>
                            <span>{s.done_races || 0}/{s.total_races || 0} races{s.phase === 'math' ? ` (${pct}%)` : ''}</span>
                          </div>
                          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full bg-sky-500 transition-all" style={{ width: `${s.phase === 'backtests' ? 100 : pct}%` }} />
                          </div>
                        </div>
                      )}
                      {s.state === 'done' && (
                        <p className="text-emerald-400 text-sm" data-testid="recompute-done">
                          ✓ {s.message || 'Recompute complete.'}
                        </p>
                      )}
                      {s.state === 'error' && (
                        <p className="text-red-400 text-sm" data-testid="recompute-error">Error: {s.message}</p>
                      )}
                      {Array.isArray(s.backtests) && s.backtests.length > 0 && (
                        <div className="mt-2 overflow-x-auto">
                          <table className="text-xs">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="text-left pr-4 py-1">Date</th>
                                <th className="text-right pr-4 py-1">TP processed / winners</th>
                                <th className="text-right py-1">Gen Tix races</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.backtests.map((b) => (
                                <tr key={b.date} className="text-slate-300">
                                  <td className="pr-4 py-0.5 font-mono">{b.date}</td>
                                  <td className="text-right pr-4 py-0.5">
                                    {b.top_performers?.error ? <span className="text-red-400">err</span> : `${b.top_performers?.processed ?? 0} / ${b.top_performers?.winners ?? 0}`}
                                  </td>
                                  <td className="text-right py-0.5">
                                    {b.generated_tix?.error ? <span className="text-red-400">err</span> : (b.generated_tix?.races ?? 0)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pool Minimums (PoolMinDefaults) curated CSV */}
            {(() => {
              const s = poolMinStatus || {};
              const r = poolMinResult;
              return (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="pool-min-card">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        Pool Minimums (Curated Defaults)
                        <span className="px-2 py-0.5 text-xs font-semibold rounded border bg-amber-500/20 text-amber-400 border-amber-500/50">
                          MANUAL
                        </span>
                      </h2>
                      <p className="text-slate-400 text-sm max-w-2xl">
                        Upload the curated <code className="text-slate-300">minpricedefaults.csv</code>. Validates the live feed's per-race pool minimum against known-valid values per track/pool, correcting feed placeholders (e.g. PID's flat $2) down to the real wager minimum.
                      </p>
                      {s.last_updated ? (
                        <p className="text-slate-500 text-xs mt-1" data-testid="pool-min-status">
                          Last updated: <span className="text-slate-300">{new Date(s.last_updated).toLocaleString()}</span>
                          {' · '}{s.rows_parsed ?? 0} rows parsed · {s.resolved_entries ?? s.cached_entries ?? 0} entries · {s.tracks ?? 0} tracks · {s.cached_entries ?? 0} cached
                        </p>
                      ) : (
                        <p className="text-slate-500 text-xs mt-1" data-testid="pool-min-status">
                          No CSV ingested yet.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={fetchPoolMinStatus}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition"
                        data-testid="pool-min-refresh-btn"
                      >
                        Refresh
                      </button>
                      <label
                        className={`px-4 py-1.5 text-white text-sm font-medium rounded-lg transition cursor-pointer ${uploadingPoolMin ? 'bg-slate-700 cursor-not-allowed' : 'bg-sky-600 hover:bg-sky-500'}`}
                        data-testid="pool-min-upload-label"
                      >
                        {uploadingPoolMin ? 'Uploading…' : 'Upload CSV'}
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          disabled={uploadingPoolMin}
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadPoolMinCsv(f); }}
                          data-testid="pool-min-file-input"
                        />
                      </label>
                    </div>
                  </div>
                  {r && (
                    <div className="mt-3 p-3 bg-slate-800 rounded-lg" data-testid="pool-min-result">
                      <p className="text-sm text-slate-300">
                        <span className="text-white font-medium">Ingested:</span> {r.rows_parsed} rows → {r.resolved_entries} resolved entries across {r.tracks} tracks · <span className="text-amber-400">{r.change_count} changed</span>
                      </p>
                      {Array.isArray(r.changes) && r.changes.length > 0 && (
                        <div className="mt-2 max-h-48 overflow-y-auto text-xs">
                          <table className="w-full text-left">
                            <thead className="text-slate-500">
                              <tr>
                                <th className="pr-4 py-1">Track</th>
                                <th className="pr-4 py-1">Pool</th>
                                <th className="pr-4 py-1">Old</th>
                                <th className="pr-4 py-1">New</th>
                                <th className="py-1">Valid (¢)</th>
                              </tr>
                            </thead>
                            <tbody className="text-slate-300">
                              {r.changes.map((c, i) => (
                                <tr key={i} className="border-t border-slate-700/50">
                                  <td className="pr-4 py-1 font-mono">{c.track}</td>
                                  <td className="pr-4 py-1 font-mono">{c.pool}</td>
                                  <td className="pr-4 py-1 text-slate-500">{c.old_recent != null ? `${c.old_recent}¢` : '—'}</td>
                                  <td className="pr-4 py-1 text-emerald-400">{c.new_recent}¢</td>
                                  <td className="py-1 text-slate-400">{(c.valid || []).join(', ')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Top Performers Job */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    Top Performers Job
                    {tpLastRun?.last_run && (
                      <span className="ml-2 text-sm font-normal text-slate-400" data-testid="tp-last-run">
                        (Last: {new Date(tpLastRun.last_run).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })} • {tpLastRun.run_type || 'Auto'})
                      </span>
                    )}
                  </h2>
                  <p className="text-slate-400 text-sm">
                    Processes completed races to calculate ROI stats • "Today" auto-updates every 30 min; 3 AM EST finalizes yesterday
                  </p>
                  {tpLastRun?.last_run && (
                    <p className="text-slate-500 text-xs mt-1">
                      Processed: {tpLastRun.processed || 0} races, {tpLastRun.winners || 0} winners
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="tp-force-label">
                    <input
                      type="checkbox"
                      checked={tpForce}
                      onChange={(e) => setTpForce(e.target.checked)}
                      className="w-4 h-4 accent-red-500"
                      data-testid="tp-force-checkbox"
                    />
                    Force reprocess (clears stored data for the date first)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={tpDate}
                      onChange={(e) => setTpDate(e.target.value)}
                      className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="tp-date-input"
                    />
                    <button
                      onClick={() => runTPJob(tpDate || null, tpForce)}
                      disabled={runningTPJob || !tpDate}
                      className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                      data-testid="tp-run-date-btn"
                    >
                      {runningTPJob ? 'Running...' : 'Run Date'}
                    </button>
                    <button
                      onClick={() => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        runTPJob(yesterday.toISOString().split('T')[0], tpForce);
                      }}
                      disabled={runningTPJob}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                      data-testid="tp-run-yesterday-btn"
                    >
                      {runningTPJob ? 'Running...' : 'Run Yesterday'}
                    </button>
                    <button
                      onClick={() => runTPJob(null, tpForce)}
                      disabled={runningTPJob}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                      data-testid="tp-run-today-btn"
                    >
                      {runningTPJob ? 'Running...' : 'Run Today'}
                    </button>
                  </div>
                </div>
              </div>
              {tpJobResult && (
                <div className="mt-3 p-3 bg-slate-800 rounded-lg" data-testid="tp-result">
                  <p className="text-sm text-slate-300">
                    <span className="text-white font-medium">Result (Rule {tpJobResult.rule_set || 'A'}):</span>{' '}
                    {(tpJobResult.races_available ?? 0) === 0
                      ? 'No completed races available for this date yet.'
                      : `${tpJobResult.new ?? 0} new, ${tpJobResult.skipped_existing ?? 0} already processed, ${tpJobResult.winners ?? 0} winners${tpJobResult.skipped_no_data ? `, ${tpJobResult.skipped_no_data} excluded (no data)` : ''}${tpJobResult.deleted ? `, ${tpJobResult.deleted} cleared first` : ''}.`}
                  </p>
                </div>
              )}
            </div>

            {/* Auto Win-Bet (CSR x STRONG) */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="autobet-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2 cursor-pointer" onClick={() => setAutobetOpen((o) => !o)} data-testid="autobet-collapse-toggle">
                    <span className="text-slate-500 text-sm">{autobetOpen ? '▾' : '▸'}</span>
                    Auto Win-Bet (Strategy A / B)
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${
                      autobetCfg?.enabled ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                                          : 'bg-red-500/20 text-red-400 border-red-500/50'}`}
                      data-testid="autobet-enabled-badge">
                      {autobetCfg?.enabled ? 'LIVE' : 'OFF'}
                    </span>
                  </h2>
                  <p className="text-slate-400 text-sm">Fires a $-configured WIN bet at 1 MTP on every runner matching an enabled strategy (Runner Dump values). Posts to the BLR endpoint.</p>
                </div>
                <button
                  onClick={() => saveAutobet({ enabled: !autobetCfg?.enabled })}
                  disabled={savingAutobet || !autobetCfg}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition ${
                    autobetCfg?.enabled ? 'bg-red-600 hover:bg-red-500 text-white'
                                        : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}
                  data-testid="autobet-toggle-btn">
                  {autobetCfg?.enabled ? 'Turn OFF' : 'Turn ON'}
                </button>
              </div>

              {autobetCfg && autobetOpen && (
                <div className="space-y-3">
                  <div className="flex items-end gap-4">
                    <button
                      onClick={() => saveAutobet({ conditions: autobetCfg.conditions })}
                      disabled={savingAutobet}
                      className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white font-medium rounded-lg transition"
                      data-testid="autobet-save-btn">
                      {savingAutobet ? 'Saving…' : 'Save wagers'}
                    </button>
                  </div>

                  {/* Bet Conditions accordion — each condition toggles on its own line with its own $ */}
                  <div className="border border-slate-800 rounded-lg" data-testid="autobet-conditions">
                    <button
                      onClick={() => setCondsOpen(!condsOpen)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800/40 transition"
                      data-testid="autobet-conditions-header">
                      <span>Strategies ({Object.values(autobetCfg.conditions || {}).filter((c) => c.enabled).length} enabled)</span>
                      <span className="text-slate-500">{condsOpen ? '▲' : '▼'}</span>
                    </button>
                    {condsOpen && (
                      <div className="divide-y divide-slate-800 border-t border-slate-800">
                        {Object.entries(autobetCfg.conditions || {}).sort((a, b) => a[0].localeCompare(b[0])).map(([k, c]) => (
                          <div key={k} className={`flex items-center gap-3 px-3 py-2 ${c.enabled ? '' : 'opacity-60'}`} data-testid={`autobet-cond-${k}`}>
                            <input
                              type="checkbox" checked={!!c.enabled}
                              onChange={(e) => {
                                const nc = { ...autobetCfg.conditions, [k]: { ...c, enabled: e.target.checked } };
                                setAutobetCfg({ ...autobetCfg, conditions: nc });
                                saveAutobet({ conditions: { [k]: { enabled: e.target.checked } } });
                              }}
                              data-testid={`autobet-cond-${k}-enabled`} />
                            <span className="text-xs text-slate-500 w-6">{k}</span>
                            <span className="flex-1 text-sm text-slate-300">{c.label}</span>
                            <div className="flex items-center gap-1.5" data-testid={`autobet-cond-${k}-fs`}>
                              <span className="text-[10px] uppercase text-slate-500">FS</span>
                              {[6, 7, 8, 9, 10, 11].map((n) => (
                                <label key={n} className="flex items-center gap-0.5 text-xs text-slate-400 cursor-pointer">
                                  <input
                                    type="checkbox" checked={(c.fs || []).includes(n)}
                                    onChange={(e) => {
                                      const fs = e.target.checked ? [...(c.fs || []), n].sort((a, b) => a - b) : (c.fs || []).filter((x) => x !== n);
                                      setAutobetCfg({ ...autobetCfg, conditions: { ...autobetCfg.conditions, [k]: { ...c, fs } } });
                                      saveAutobet({ conditions: { [k]: { fs } } });
                                    }}
                                    data-testid={`autobet-cond-${k}-fs-${n}`} />
                                  {n}
                                </label>
                              ))}
                            </div>
                            <span className="text-slate-500 text-sm">$</span>
                            <input
                              type="number" min="0" step="1"
                              value={c.wager ?? 0}
                              onChange={(e) => {
                                const nc = { ...autobetCfg.conditions, [k]: { ...c, wager: e.target.value } };
                                setAutobetCfg({ ...autobetCfg, conditions: nc });
                              }}
                              className="w-20 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-white"
                              data-testid={`autobet-cond-${k}-wager`} />
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="px-3 py-2 text-xs text-slate-600 border-t border-slate-800">
                      Every runner matching an <b>enabled</b> strategy with a $ above 0 fires (ticket tagged -A / -B), only in fields whose FS box is ticked (fields outside 6–11 never fire). A and B can never match the same horse. Toggles save instantly; edit a $ then click <b>Save wagers</b>.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={!!autobetCfg.override}
                      onChange={(e) => saveAutobet({ override: e.target.checked })}
                      data-testid="autobet-override-chk" />
                    Override (re-fire even if a ticket already exists for a horse — use only to re-consume after scratches)
                  </label>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    {autobet?.days?.length > 0 && (
                      <select
                        value={autobetDay || ''}
                        onChange={(e) => fetchAutobet(e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200"
                        data-testid="autobet-day-select">
                        {autobet.days.map((d) => (
                          <option key={d.day} value={d.day}>
                            {d.day}{d.day === autobet.today ? ' (today)' : ''} · {d.count} bet{d.count === 1 ? '' : 's'}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {autobet?.summary && (
                    <div className="text-xs mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="autobet-summary">
                      <span className="text-slate-400">Bets: <span className="text-slate-200 font-semibold">{autobet.summary.bets}</span></span>
                      <span className="text-slate-400">Settled: <span className="text-slate-200 font-semibold">{autobet.summary.settled}</span> ({autobet.summary.wins} W)</span>
                      <span className="text-slate-400">Pending: <span className="text-slate-200 font-semibold">{autobet.summary.pending}</span></span>
                      <span className="text-slate-400">Staked: <span className="text-slate-200 font-semibold">${autobet.summary.staked.toFixed(2)}</span></span>
                      <span className="text-slate-400">Returned: <span className="text-slate-200 font-semibold">${autobet.summary.returned.toFixed(2)}</span></span>
                      <span className="text-slate-400">Net: <span className={`font-semibold ${autobet.summary.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{autobet.summary.net >= 0 ? '+' : ''}${autobet.summary.net.toFixed(2)}</span></span>
                      {autobet.summary.roi_pct != null && (
                        <span className="text-slate-400">ROI: <span className={`font-semibold ${autobet.summary.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{autobet.summary.roi_pct >= 0 ? '+' : ''}{autobet.summary.roi_pct}%</span></span>
                      )}
                      {autobet.summary.simulated > 0 && (
                        <span className="text-slate-500 italic" data-testid="autobet-sim-summary">Simulated (not in totals): {autobet.summary.simulated} · net {autobet.summary.sim_net >= 0 ? '+' : ''}${autobet.summary.sim_net.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  {autobet?.recent?.length > 0 ? (
                    <div className="mt-2 max-h-48 overflow-auto border border-slate-800 rounded-lg" data-testid="autobet-recent">
                      <table className="w-full text-xs">
                        <thead className="text-slate-500 bg-slate-950 sticky top-0">
                          <tr><th className="text-left px-2 py-1">Ticket</th><th className="text-left px-2 py-1">Track</th><th className="px-2 py-1">Race</th><th className="px-2 py-1">Horse</th><th className="px-2 py-1">Cond</th><th className="px-2 py-1">$</th><th className="px-2 py-1">Sent</th><th className="px-2 py-1">Outcome</th></tr>
                        </thead>
                        <tbody>
                          {autobet.recent.map((t) => (
                            <tr key={t.ticket_id} className={`border-t border-slate-800 ${t.simulated ? 'text-slate-500 italic' : 'text-slate-300'}`} data-testid={`autobet-row-${t.ticket_id}`}>
                              <td className="px-2 py-1 font-mono">{t.ticket_id}</td>
                              <td className="px-2 py-1">{t.track_feed}</td>
                              <td className="px-2 py-1 text-center">{t.race}</td>
                              <td className="px-2 py-1 text-center">{t.program}</td>
                              <td className="px-2 py-1 text-center">{t.condition}</td>
                              <td className="px-2 py-1 text-center">${(t.stake_cents / 100).toFixed(0)}</td>
                              <td className="px-2 py-1 text-center" title={t.result?.ok ? 'Accepted by endpoint' : (t.result?.error || `HTTP ${t.result?.status_code}` || 'not sent')} data-testid={`autobet-sent-${t.ticket_id}`}>
                                {t.simulated ? <span className="text-slate-500" title="Simulated (FS disabled) — not sent">SIM</span> : t.result?.ok ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">✗</span>}
                              </td>
                              <td className="px-2 py-1 text-center" data-testid={`autobet-outcome-${t.ticket_id}`}>
                                {(() => {
                                  const g = t.grade || {};
                                  if (g.outcome === 'win') return <span className="text-emerald-400 font-semibold">Win ${Number(g.returned).toFixed(2)}</span>;
                                  if (g.outcome === 'loss') return <span className="text-red-400">Loss</span>;
                                  return <span className="text-slate-500">Pending</span>;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-500" data-testid="autobet-empty">No auto-bets on {autobetDay || 'this day'}.</div>
                  )}
                </div>
              )}
            </div>

          </>
        )}

        {/* Env / Settings Tab (moved out of Jobs — these are config, not jobs) */}
        {activeTab === 'env' && (
          <>
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-white mb-1">Environment &amp; Settings</h1>
              <p className="text-slate-400 text-sm">App-wide configuration and display controls (not scheduled jobs).</p>
            </div>

            <AvoidListCard />
            <GoodTrainersCard />

            {/* Global Announcement Message */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="global-message-control">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-[240px]">
                  <h2 className="text-lg font-semibold text-white">Global Message</h2>
                  <p className="text-slate-400 text-sm">
                    Free-form banner shown to <b>all</b> users (login page + inside the app). Leave blank to hide.
                    Users can dismiss it; it reappears when you edit the text or severity.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded font-medium ${globalMsg.message && globalMsg.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'}`} data-testid="global-message-status">
                    {globalMsg.message && globalMsg.active ? 'Live' : 'Hidden'}
                  </span>
                </div>
              </div>

              <textarea
                data-testid="global-message-input"
                value={globalMsg.message}
                onChange={(e) => setGlobalMsg((p) => ({ ...p, message: e.target.value }))}
                rows={3}
                placeholder="e.g. Scheduled maintenance tonight 11pm–1am ET. Downloads may be briefly unavailable."
                className="mt-3 w-full px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500 resize-y"
              />

              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1 text-sm text-slate-400">
                  <span className="mr-1 text-slate-500">Severity:</span>
                  <select
                    data-testid="global-message-severity"
                    value={globalMsg.severity}
                    onChange={(e) => setGlobalMsg((p) => ({ ...p, severity: e.target.value }))}
                    className="px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                  >
                    <option value="info">Info (blue)</option>
                    <option value="warning">Warning (amber)</option>
                    <option value="critical">Critical (red)</option>
                  </select>
                </div>
                <button
                  onClick={() => setGlobalMsg((p) => ({ ...p, active: !p.active }))}
                  className={`px-4 py-2 rounded-lg font-medium transition ${globalMsg.active ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`}
                  data-testid="global-message-active-toggle"
                >
                  {globalMsg.active ? 'Active' : 'Inactive'}
                </button>
                <button
                  onClick={saveGlobalMsg}
                  disabled={savingGlobalMsg}
                  className="px-4 py-2 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition disabled:opacity-50 ml-auto"
                  data-testid="global-message-save-btn"
                >
                  {savingGlobalMsg ? 'Saving…' : 'Save & Publish'}
                </button>
                <button
                  onClick={() => { setGlobalMsg((p) => ({ ...p, message: '' })); }}
                  className="px-3 py-2 rounded-lg font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  data-testid="global-message-clear-btn"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Ticker Display Control */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="ticker-control">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">AutoGen Results Ticker</h2>
                  <p className="text-slate-400 text-sm">
                    Controls which day the public ROI ticker shows — authoritative for all users.
                  </p>
                  <p className="text-sm mt-1" data-testid="ticker-current">
                    <span className="text-slate-500">Currently showing: </span>
                    <span className="font-bold text-amber-400">
                      {tickerSetting ? (tickerSetting.view === 'today' ? 'Today' : 'Yesterday') : '…'}
                      {tickerSetting?.race_date ? ` (${tickerSetting.race_date})` : ''}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateTickerView('yesterday')}
                    disabled={savingTicker || tickerSetting?.view === 'yesterday'}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      tickerSetting?.view === 'yesterday'
                        ? 'bg-amber-600 text-white cursor-default'
                        : 'bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-50'
                    }`}
                    data-testid="ticker-set-yesterday-btn"
                  >
                    Show Yesterday
                  </button>
                  <button
                    onClick={() => updateTickerView('today')}
                    disabled={savingTicker || tickerSetting?.view === 'today'}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      tickerSetting?.view === 'today'
                        ? 'bg-emerald-600 text-white cursor-default'
                        : 'bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-50'
                    }`}
                    data-testid="ticker-set-today-btn"
                  >
                    Show Today
                  </button>
                </div>
              </div>
            </div>

            {/* Exacta Expected Value chiclet */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="exacta-ev-control">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-[240px]">
                  <h2 className="text-lg font-semibold text-white">Exacta Expected Value</h2>
                  <p className="text-slate-400 text-sm">
                    Visibility of the <b>Exp. ROI / Exp. Return</b> readout after an Exacta Auto Generate
                    (IntelliTix &amp; iPro). Independent of the Exacta Value page. Prices off the latest cycle.
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    <b>Everyone</b> = all logged-in users · <b>Entitled</b> = admins + users with Exacta Value access ·
                    <b> Admins</b> = admins only · <b>Off</b> = hidden for all.
                  </p>
                </div>
                <select
                  value={exactaEvMode || ''}
                  onChange={(e) => updateExactaEvSetting(e.target.value)}
                  disabled={savingExactaEv || exactaEvMode === null}
                  className="px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                  data-testid="exacta-ev-mode-select"
                >
                  {exactaEvMode === null && <option value="">…</option>}
                  <option value="everyone">Everyone</option>
                  <option value="entitled">Entitled users only</option>
                  <option value="admins">Admins only</option>
                  <option value="off">Off</option>
                </select>
              </div>
            </div>

            {/* Widget Feature Flags */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="widget-features-control">
              <h2 className="text-lg font-semibold text-white">Embeddable Widget Features</h2>
              <p className="text-slate-400 text-sm mb-3">
                Show or hide optional controls in the public QuickPick widget (<code className="text-slate-300">/widget</code>). Applies to all embedded widgets.
              </p>

              {/* Late Signal toggle */}
              <div className="flex items-center justify-between py-2 border-t border-slate-800">
                <div>
                  <p className="text-white font-medium">Late Signal Indicators</p>
                  <p className="text-slate-500 text-sm">Strong / Fade (Must-Use / Don't-Use) row cues + legend.</p>
                </div>
                <button
                  onClick={() => updateWidgetSetting('showLateSignal', !widgetSettings?.showLateSignal)}
                  disabled={savingWidget || !widgetSettings}
                  className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${
                    widgetSettings?.showLateSignal
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                  }`}
                  data-testid="widget-late-signal-toggle"
                >
                  {widgetSettings?.showLateSignal ? 'Shown' : 'Hidden'}
                </button>
              </div>

              {/* Straight Bets toggle */}
              <div className="flex items-center justify-between py-2 border-t border-slate-800">
                <div>
                  <p className="text-white font-medium">Straight Bet Options</p>
                  <p className="text-slate-500 text-sm">Win / Win+Place wagers + "My Keys Only" control.</p>
                </div>
                <button
                  onClick={() => updateWidgetSetting('showStraightBets', !widgetSettings?.showStraightBets)}
                  disabled={savingWidget || !widgetSettings}
                  className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${
                    widgetSettings?.showStraightBets
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                  }`}
                  data-testid="widget-straight-bets-toggle"
                >
                  {widgetSettings?.showStraightBets ? 'Shown' : 'Hidden'}
                </button>
              </div>
            </div>

            {/* Pool-Score Algorithm */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="calc-algorithm-control">
              <h2 className="text-lg font-semibold text-white">Default Algorithm</h2>
              <p className="text-slate-400 text-sm mb-1">
                The <b>fallback</b> scoring formula (pool scores, consensus, Win Rank, Strong/Fade). Used by <b>BetMaker</b> and any widget client set to <b>"Inherit"</b>. Individual widget clients can override this in <b>Widget Clients</b>. Read-time — switching applies to inheriting consumers <b>immediately</b>.
              </p>
              <p className="text-amber-400/80 text-xs mb-3">
                Note: the IntelliTix main app now runs Rule Set C + Logarithmic (set on its client). This default only affects BetMaker and "Inherit" clients.
              </p>
              <div className="flex items-center justify-between py-2 border-t border-slate-800">
                <div>
                  <p className="text-white font-medium">Active algorithm</p>
                  <p className="text-slate-500 text-sm">Currently: <b className="text-slate-200 capitalize">{calcAlgo || '—'}</b></p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateCalcAlgo('original')}
                    disabled={savingAlgo || !calcAlgo || calcAlgo === 'original'}
                    className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${calcAlgo === 'original' ? 'bg-emerald-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`}
                    data-testid="calc-algo-original"
                  >Original</button>
                  <button
                    onClick={() => updateCalcAlgo('logarithmic')}
                    disabled={savingAlgo || !calcAlgo || calcAlgo === 'logarithmic'}
                    className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${calcAlgo === 'logarithmic' ? 'bg-emerald-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'}`}
                    data-testid="calc-algo-logarithmic"
                  >Logarithmic</button>
                </div>
              </div>
            </div>

            {/* Ticket Consolidation Thresholds */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-4" data-testid="consolidation-settings-control">
              <h2 className="text-lg font-semibold text-white">Ticket Consolidation</h2>
              <p className="text-slate-400 text-sm mb-3">
                In downloaded ADW files, collapse combos into wheels only when a pool's ticket count
                <span className="text-slate-300"> exceeds</span> the threshold below (keeps small tickets readable as raw combos).
                Reverse-pair boxing and the $2 single-exacta minimum always apply. Set <code className="text-slate-300">0</code> to always consolidate. Applies to BetMaker &amp; IntelliTix.
              </p>
              <div className="flex flex-wrap items-end gap-4">
                {['EX', 'TRI', 'SPR'].map((pool) => (
                  <div key={pool} className="flex flex-col">
                    <label className="text-slate-400 text-xs mb-1">{pool} — consolidate above</label>
                    <input
                      type="number" min="0"
                      value={consolDraft[pool]}
                      onChange={(e) => setConsolDraft((d) => ({ ...d, [pool]: e.target.value }))}
                      className="w-28 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-right"
                      data-testid={`consol-threshold-${pool}`}
                    />
                  </div>
                ))}
                <button
                  onClick={saveConsolThresholds}
                  disabled={savingConsol}
                  className="px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="consol-save-btn"
                >
                  {savingConsol ? 'Saving…' : 'Save Thresholds'}
                </button>
                <span className="text-slate-500 text-xs">
                  Current: EX&gt;{consolThresholds.EX} · TRI&gt;{consolThresholds.TRI} · SPR&gt;{consolThresholds.SPR}
                </span>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-800">
                <p className="text-slate-500 text-xs mb-2">
                  Horizontal (multi-race) pools — <span className="text-amber-500/80">config only; applied once horizontal ticket generation is added.</span>
                </p>
                <div className="flex flex-wrap items-end gap-4">
                  {['DBL', 'P3', 'P4', 'P5', 'P6'].map((pool) => (
                    <div key={pool} className="flex flex-col">
                      <label className="text-slate-400 text-xs mb-1">{pool} — consolidate above</label>
                      <input
                        type="number" min="0"
                        value={consolDraft[pool]}
                        onChange={(e) => setConsolDraft((d) => ({ ...d, [pool]: e.target.value }))}
                        className="w-24 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-right"
                        data-testid={`consol-threshold-${pool}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </>
        )}

        {/* Downloads Tab */}
        {activeTab === 'downloads' && (
          <>
            <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Download Activity</h1>
                <p className="text-slate-400 text-sm flex items-center flex-wrap gap-x-3 gap-y-1" data-testid="download-totals">
                  <span>Total downloads: <span className="text-emerald-400 font-medium">{visibleDownloads.length}</span></span>
                  {Object.entries(formatTotals).sort().map(([fmt, amt]) => (
                    <span key={fmt} className="capitalize">{fmt}: <span className="text-emerald-400 font-medium">${amt.toFixed(2)}</span></span>
                  ))}
                </p>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <span className="mr-1 text-slate-500">Race date:</span>
                  <input
                    type="date"
                    data-testid="download-start-date"
                    value={downloadStartDate}
                    max={downloadEndDate}
                    onChange={(e) => { setDownloadStartDate(e.target.value); setExpandedDownload(null); }}
                    className="px-2 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    data-testid="download-end-date"
                    value={downloadEndDate}
                    min={downloadStartDate}
                    onChange={(e) => { setDownloadEndDate(e.target.value); setExpandedDownload(null); }}
                    className="px-2 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <select
                  data-testid="download-user-filter"
                  value={downloadUserFilter}
                  onChange={(e) => { setDownloadUserFilter(e.target.value); setExpandedDownload(null); }}
                  className="px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">All users</option>
                  {[...new Map(downloads.filter((d) => d.user_id).map((d) => [d.user_id, d.user_email])).entries()]
                    .map(([id, email]) => ({ id, email: email || id }))
                    .sort((a, b) => (a.email || '').localeCompare(b.email || ''))
                    .map((u) => (<option key={u.id} value={u.id}>{u.email}</option>))}
                </select>
                <select
                  data-testid="download-summary-mode"
                  value={downloadSummaryMode}
                  onChange={(e) => setDownloadSummaryMode(e.target.value)}
                  className="px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                >
                  <option value="pool">By Pool</option>
                  <option value="track">By Track</option>
                  <option value="trackpool">By Track / Pool</option>
                </select>
                <button
                  onClick={fetchDownloads}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Summary — Wagered · Payouts · ROI grouped by pool / track / track+pool */}
            {(() => {
              const rows = downloadSummary.rows;
              if (!rows.length && !poolGrading.loading) return null;
              const tot = rows.reduce((a, b) => {
                a.wagered += b.wagered; a.returned += b.returned; a.gradedWagered += b.gradedWagered;
                a.graded += b.graded; a.total += b.total; return a;
              }, { wagered: 0, returned: 0, gradedWagered: 0, graded: 0, total: 0 });
              const totRoi = tot.gradedWagered > 0 ? ((tot.returned - tot.gradedWagered) / tot.gradedWagered) * 100 : 0;
              const modeLabel = downloadSummaryMode === 'track' ? 'By Track'
                : downloadSummaryMode === 'trackpool' ? 'By Track / Pool' : 'By Pool';
              const colHead = downloadSummaryMode === 'track' ? 'Track'
                : downloadSummaryMode === 'trackpool' ? 'Track / Pool' : 'Pool';
              return (
                <div className="mb-4 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="downloads-by-pool">
                  <div className="px-4 py-2.5 text-sm font-semibold text-slate-200 border-b border-slate-800 flex items-center justify-between">
                    <span>{modeLabel} — Wagered · Payouts · ROI</span>
                    {poolGrading.loading && <span className="text-xs text-slate-500 font-normal">grading…</span>}
                    {!poolGrading.loading && downloadSummary.ungraded > 0 && (
                      <span className="text-xs text-amber-400 font-normal">{downloadSummary.ungraded} not yet graded (payouts unavailable) — excluded from ROI</span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-slate-400 text-xs uppercase tracking-wide bg-slate-800/40">
                          <th className="text-left px-4 py-2 font-semibold">{colHead}</th>
                          <th className="text-right px-3 py-2 font-semibold"># Downloads</th>
                          <th className="text-right px-3 py-2 font-semibold">Wagered</th>
                          <th className="text-right px-3 py-2 font-semibold">Payouts</th>
                          <th className="text-right px-4 py-2 font-semibold">ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((b) => (
                          <tr key={b.key} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`downloads-pool-${b.key}`}>
                            <td className="px-4 py-2">
                              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium capitalize">{b.label}</span>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-300">{b.total}</td>
                            <td className="px-3 py-2 text-right text-slate-200 font-medium">${b.wagered.toFixed(2)}</td>
                            <td className={`px-3 py-2 text-right font-medium ${b.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${b.returned.toFixed(2)}</td>
                            <td className={`px-4 py-2 text-right font-bold ${b.gradedWagered <= 0 ? 'text-slate-500' : b.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`downloads-pool-roi-${b.key}`}>
                              {b.gradedWagered <= 0 ? 'n/a' : `${b.roi >= 0 ? '+' : ''}${b.roi.toFixed(0)}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {rows.length > 1 && (
                        <tfoot>
                          <tr className="border-t-2 border-slate-700 bg-slate-800/30 font-semibold">
                            <td className="px-4 py-2 text-slate-200">Total</td>
                            <td className="px-3 py-2 text-right text-slate-300">{tot.total}</td>
                            <td className="px-3 py-2 text-right text-slate-100">${tot.wagered.toFixed(2)}</td>
                            <td className={`px-3 py-2 text-right ${tot.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${tot.returned.toFixed(2)}</td>
                            <td className={`px-4 py-2 text-right ${tot.gradedWagered <= 0 ? 'text-slate-500' : totRoi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="downloads-pool-roi-total">
                              {tot.gradedWagered <= 0 ? 'n/a' : `${totRoi >= 0 ? '+' : ''}${totRoi.toFixed(0)}%`}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* 7-day detail cap info bar */}
            {downloadsCapped && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-700 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="downloads-cap-info">
                <span className="mt-0.5">ℹ️</span>
                <span>The summary above reflects the full selected range. The detail list below shows only the most recent 7 days (from {downloadsCutoff}) to keep the page fast. Narrow the race-date range to see older detail rows.</span>
              </div>
            )}

            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800/50 border-b border-slate-700">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300 w-6"></th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Time</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">User</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Track</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Race</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Race Date</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Pool</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Format</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Tickets</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Wager</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const visible = cappedDownloads;
                      if (loadingDownloads) {
                        return (<tr><td colSpan="11" className="px-4 py-8 text-center text-slate-400">Loading downloads...</td></tr>);
                      }
                      if (visible.length === 0) {
                        return (<tr><td colSpan="11" className="px-4 py-8 text-center text-slate-400">No downloads recorded yet</td></tr>);
                      }
                      return visible.map((d) => {
                        const open = expandedDownload === d.id;
                        const det = downloadDetails[d.id];
                        const dPayouts = (d.tickets || []).length && d.race_date
                          ? (payoutsByRace[`${d.track}|${d.race_number}|${d.race_date}`] || [])
                          : [];
                        let dRoi = null;
                        if (dPayouts.length) {
                          const w = (d.tickets || []).reduce((s, t) => s + (t.amount || 0), 0);
                          const r = (d.tickets || []).reduce((s, t) => s + gradeTicket(t, dPayouts), 0);
                          dRoi = w > 0 ? ((r - w) / w) * 100 : null;
                        }
                        return (
                          <React.Fragment key={d.id}>
                            <tr
                              className="border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer"
                              onClick={() => toggleDownloadDetail(d)}
                              data-testid={`download-row-${d.id}`}
                            >
                              <td className="px-4 py-3 text-slate-500 text-sm">{open ? '▾' : '▸'}</td>
                              <td className="px-4 py-3 text-slate-400 text-sm">{d.downloaded_at ? formatLocalDateTime(d.downloaded_at) : 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-300 text-sm">{d.user_email || 'anonymous'}</td>
                              <td className="px-4 py-3 text-white font-medium text-sm">{d.track}</td>
                              <td className="px-4 py-3 text-slate-300 text-sm">R{d.race_number}</td>
                              <td className="px-4 py-3 text-sm font-medium text-amber-300" data-testid={`download-racedate-${d.id}`}>{d.race_date || <span className="text-slate-500">—</span>}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">{d.pool}</span>
                              </td>
                              <td className="px-4 py-3 text-slate-300 text-sm capitalize">{d.format}</td>
                              <td className="px-4 py-3 text-slate-300 text-sm">{d.ticket_count}</td>
                              <td className="px-4 py-3 text-emerald-400 font-medium text-sm">${d.total_wager?.toFixed(2)}</td>
                              <td className={`px-4 py-3 text-right font-bold text-sm ${dRoi === null ? 'text-slate-500' : dRoi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`download-roi-${d.id}`}>
                                {dRoi === null ? 'n/a' : `${dRoi >= 0 ? '+' : ''}${dRoi.toFixed(0)}%`}
                              </td>
                            </tr>
                            {open && (
                              <tr className="bg-slate-950/60 border-b border-slate-800" data-testid={`download-detail-${d.id}`}>
                                <td colSpan="11" className="px-6 py-4">
                                  {det?.loading ? (
                                    <div className="text-slate-400 text-sm">Loading wager details…</div>
                                  ) : det?.noTickets ? (
                                    <div className="text-slate-500 text-sm italic">Ticket details were not recorded for this download (older entry).</div>
                                  ) : det ? (
                                    <div>
                                      <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
                                        <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1"><span className="text-slate-400">Wagered </span><span className="font-bold text-white">${det.wagered.toFixed(2)}</span></span>
                                        {det.graded ? (
                                          <>
                                            <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1"><span className="text-slate-400">Returned </span><span className={`font-bold ${det.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${det.returned.toFixed(2)}</span></span>
                                            <span className={`rounded px-2 py-1 border ${det.roi >= 0 ? 'bg-emerald-500/10 border-emerald-700' : 'bg-red-500/10 border-red-800'}`}><span className="text-slate-400">ROI </span><span className={`font-bold ${det.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`download-roi-${d.id}`}>{det.roi >= 0 ? '+' : ''}{det.roi.toFixed(0)}%</span></span>
                                          </>
                                        ) : (
                                          <span className="rounded bg-amber-500/10 border border-amber-700 text-amber-400 px-2 py-1">Not yet graded — race payouts unavailable</span>
                                        )}
                                      </div>
                                      {d.inputs && (() => {
                                        const inp = d.inputs;
                                        const pns = [...new Set([
                                          ...Object.keys(inp.myOdds || {}), ...Object.keys(inp.dontUse || {}),
                                          ...Object.keys(inp.keySel || {}), ...Object.keys(inp.grid || {}),
                                          ...Object.keys(inp.bestFin || {}), ...Object.keys(inp.worstFin || {}),
                                          ...Object.keys(inp.straightBet || {}),
                                        ])].sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
                                        const POSL = { 0: 'Any', 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' };
                                        return (
                                          <div className="mb-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3" data-testid={`download-inputs-${d.id}`}>
                                            <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">Inputs used</div>
                                            <div className="flex flex-wrap gap-2 text-[11px] mb-2">
                                              <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Mode: <b className="text-white capitalize">{inp.mode || '—'}</b></span>
                                              <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Coverage: <b className="text-white">{inp.coverage === 'exclude' ? 'Exclude Low-Prob' : 'Full'}</b></span>
                                              <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Odds: <b className="text-white capitalize">{inp.oddsSource || '—'}</b>{inp.blendPct ? ` (blend ${inp.blendPct}%)` : ''}</span>
                                              {Array.isArray(inp.pools) && inp.pools.length > 0 && <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Pools: <b className="text-white">{inp.pools.join(', ')}</b></span>}
                                            </div>
                                            {pns.length > 0 ? (
                                              <table className="text-[11px] w-auto">
                                                <thead><tr className="text-slate-500">
                                                  <th className="text-left pr-4">#</th><th className="text-right pr-4">MyOdds</th>
                                                  <th className="text-center pr-4">Key</th>
                                                  <th className="text-center pr-4">Best</th><th className="text-center pr-4">Worst</th>
                                                  <th className="text-center pr-4">Positions</th>
                                                  <th className="text-center pr-4">Straight</th><th className="text-center">Dont-Use</th>
                                                </tr></thead>
                                                <tbody>
                                                  {pns.map((pn) => (
                                                    <tr key={pn} className="text-slate-300" data-testid={`download-input-row-${d.id}-${pn}`}>
                                                      <td className="pr-4 font-bold text-white">{pn}</td>
                                                      <td className="text-right pr-4">{inp.myOdds?.[pn] ?? '—'}</td>
                                                      <td className="text-center pr-4 text-amber-400">{inp.keySel?.[pn] ? '★' : ''}</td>
                                                      <td className="text-center pr-4">{POSL[inp.bestFin?.[pn] || 0]}</td>
                                                      <td className="text-center pr-4">{inp.worstFin?.[pn] ? POSL[inp.worstFin[pn]] : 'Any'}</td>
                                                      <td className="text-center pr-4">{Array.isArray(inp.grid?.[pn]) && inp.grid[pn].length ? inp.grid[pn].slice().sort((a, b) => a - b).map((p) => POSL[p]).join('/') : '—'}</td>
                                                      <td className="text-center pr-4">{inp.straightBet?.[pn] || '—'}</td>
                                                      <td className="text-center text-red-400">{inp.dontUse?.[pn] ? '✓' : ''}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            ) : <div className="text-slate-500 text-[11px] italic">No per-runner overrides (defaults used).</div>}
                                          </div>
                                        );
                                      })()}
                                      <div className="flex flex-wrap gap-1.5 font-mono text-xs">
                                        {det.rows.map((r, i) => {
                                          const straight = /^(WIN|PLC|PLACE)/i.test(r.combo || '');
                                          return (
                                          <span key={i} className={`px-2 py-1 rounded border ${r.win ? 'bg-emerald-500/15 border-emerald-700' : 'bg-slate-800 border-slate-700'}`}>
                                            {!straight && <><span className="text-slate-500">{r.pool}</span>{' '}</>}
                                            <span className={r.win ? 'text-emerald-300 font-bold' : 'text-slate-200'}>{r.combo}</span>{' '}
                                            <span className="text-amber-400">${r.amount.toFixed(2)}</span>
                                            {det.graded && r.win && <span className="text-emerald-400 font-semibold"> ✓${r.ret.toFixed(2)}</span>}
                                          </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Generated Tix Tab */}
        {activeTab === 'signals' && (
          <div data-testid="signals-tab-content"><SignalEffects /></div>
        )}

        {activeTab === 'gentix' && (
          <div data-testid="gentix-panel">
            <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">Generated Tix</h1>
                <p className="text-slate-400 text-sm">
                  Daily Exacta Auto-Generate sim graded with the app's weighted stakes, across every Rule Set × Algorithm combo. "Run now" processes
                  the selected date range (max 31 days); already-computed days are skipped unless "Recompute" is checked.
                  {gtSummary?.last_run && (
                    <span className="ml-2 text-slate-500">Last run: {gtSummary.last_run.sim_date} ({gtSummary.last_run.races} races)</span>
                  )}
                </p>
                {gtRunMsg && (
                  <p className="text-emerald-400 text-xs mt-1 whitespace-pre-line" data-testid="gentix-run-msg">{gtRunMsg}</p>
                )}
                {gtSummary?.single_day && gtSummary?.provenance && (
                  <p className="text-xs mt-1" data-testid="gentix-provenance">
                    <span className="text-slate-500">Live that day:</span>{' '}
                    <span className="font-semibold text-amber-300">
                      Rule {gtSummary.provenance.live_rule_set} / {gtSummary.provenance.live_algo === 'logarithmic' ? 'Logarithmic' : 'Original'}
                    </span>
                    <span className="text-slate-600"> · logic {gtSummary.provenance.logic_version}</span>
                  </p>
                )}
                {gtSummary?.single_day && gtSummary?.provenance === null && (
                  <p className="text-xs mt-1 text-slate-600" data-testid="gentix-provenance-missing">
                    Live config not recorded for this day (pre‑provenance) — LIVE badge reflects the current setting.
                  </p>
                )}
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <input type="date" data-testid="gentix-start" value={gtStart} max={gtEnd}
                  onChange={(e) => { setGtStart(e.target.value); setGtDirty(true); }}
                  className={`px-2 py-2 bg-slate-800 border text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500 ${gtDirty ? 'border-amber-500' : 'border-slate-700'}`} />
                <span className="text-slate-400 text-xs">to</span>
                <input type="date" data-testid="gentix-end" value={gtEnd} min={gtStart}
                  onChange={(e) => { setGtEnd(e.target.value); setGtDirty(true); }}
                  className={`px-2 py-2 bg-slate-800 border text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500 ${gtDirty ? 'border-amber-500' : 'border-slate-700'}`} />
                <button onClick={applyGtRange} data-testid="gentix-apply"
                  className={`px-4 py-2 font-medium rounded-lg transition text-white ${gtDirty ? 'bg-amber-500 hover:bg-amber-400 ring-2 ring-amber-300/60 animate-pulse' : 'bg-slate-700 hover:bg-slate-600'}`}>
                  {gtDirty ? 'Apply ↵' : 'Apply'}</button>
                <button onClick={downloadGtCsv} data-testid="gentix-csv"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition">Download CSV</button>
                <label className="flex items-center gap-1.5 text-slate-300 text-xs cursor-pointer select-none px-1" data-testid="gentix-recompute-label">
                  <input type="checkbox" data-testid="gentix-recompute" checked={gtRecompute}
                    onChange={(e) => setGtRecompute(e.target.checked)}
                    className="accent-emerald-500 w-4 h-4" />
                  Recompute
                </label>
                <button onClick={runGtSim} disabled={gtRunning} data-testid="gentix-run"
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition disabled:opacity-50">
                  {gtRunning ? 'Running…' : 'Run now'}</button>
                <button onClick={downloadPerfReport} disabled={perfBusy} data-testid="gentix-perf-pdf"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg transition disabled:opacity-50">
                  {perfBusy ? 'Building…' : 'Performance PDF'}</button>
              </div>
            </div>
            {perfMsg && (
              <p className="text-indigo-300 text-xs mt-1 whitespace-pre-line px-1" data-testid="gentix-perf-msg">{perfMsg}</p>
            )}

            {gtLoading ? (
              <div className="text-slate-400 py-8 text-center">Loading…</div>
            ) : gtSummary ? (
              <>
              {/* Strong Signals table — All + Visible per algo */}
              <div className="mb-4 bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden" data-testid="gentix-strong-signals">
                <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-2 flex-wrap">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-sm font-semibold text-amber-300">Strong Signals</span>
                  <span className="text-xs text-slate-500">$2 win-bet on every STRONG (must-use) runner · algo-dependent{gtSummary.single_day ? ' · click a row for runner detail' : ''}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs uppercase tracking-wide bg-slate-900/40">
                        <th className="text-left px-4 py-2 font-semibold">Algorithm</th>
                        <th className="text-left px-3 py-2 font-semibold">Scope</th>
                        <th className="text-right px-3 py-2 font-semibold"># Signals</th>
                        <th className="text-right px-3 py-2 font-semibold">Win %</th>
                        <th className="text-right px-4 py-2 font-semibold">$2-Win ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(gtSummary.strong || []).map((s) => {
                        const key = `${s.algo}|${s.scope}`;
                        const open = ssExpandedKey === key;
                        const det = ssDetailMap[key];
                        const runners = det ? (Object.values(det.runners_by_date || {})[0] || []) : [];
                        return (
                        <React.Fragment key={key}>
                        <tr
                          className={`border-t border-slate-800 ${s.is_live ? 'bg-amber-500/10' : ''} ${gtSummary.single_day ? 'cursor-pointer hover:bg-slate-800/40' : ''}`}
                          data-testid={`strong-row-${s.algo}-${s.scope}`}
                          onClick={() => gtSummary.single_day && toggleStrongRow(s.algo, s.scope)}
                        >
                          <td className={`px-4 py-2 font-medium ${s.is_live ? 'text-amber-200' : 'text-slate-200'}`}>
                            <span className="inline-flex items-center gap-2">
                              {gtSummary.single_day && <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>}
                              {algoLabel(s.algo)}
                              {s.is_live && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-600/50">LIVE</span>}
                            </span>
                          </td>
                          <td className={`px-3 py-2 uppercase text-xs tracking-wide font-semibold ${s.scope === 'visible' ? 'text-teal-300' : 'text-slate-400'}`}>{s.scope}</td>
                          <td className="px-3 py-2 text-right text-slate-300">{s.count}</td>
                          <td className="px-3 py-2 text-right text-amber-300 font-semibold">{s.win_pct}%</td>
                          <td className={`px-4 py-2 text-right font-bold ${s.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{s.roi >= 0 ? '+' : ''}{s.roi}%</td>
                        </tr>
                        {open && (
                          <tr className="bg-slate-900/50" data-testid={`strong-detail-${s.algo}-${s.scope}`}>
                            <td colSpan={5} className="px-4 py-3">
                              {ssDetailLoading && !det ? (
                                <div className="text-slate-400 text-sm py-2 text-center">Loading…</div>
                              ) : runners.length ? (
                                <table className="w-full text-xs">
                                  <thead><tr className="text-slate-500 uppercase tracking-wide">
                                    <th className="text-left py-1 font-medium">Track</th><th className="text-left py-1 font-medium">Race</th><th className="text-left py-1 font-medium">Runner</th><th className="text-left py-1 font-medium">Result</th><th className="text-right py-1 font-medium">$2 → Return</th>
                                  </tr></thead>
                                  <tbody>
                                    {runners.map((rn, i) => (
                                      <tr key={i} className="border-t border-slate-800/60">
                                        <td className="py-1 font-mono text-slate-300">{rn.track}</td>
                                        <td className="py-1 text-slate-400">R{rn.race_number}</td>
                                        <td className="py-1 font-mono text-slate-200">#{rn.program_number}</td>
                                        <td className="py-1">{rn.won ? <span className="text-emerald-400 font-semibold">WON 1st</span> : <span className="text-slate-500">—</span>}</td>
                                        <td className={`py-1 text-right font-mono ${rn.won ? 'text-emerald-400' : 'text-slate-500'}`}>{rn.won ? `$${rn.returned.toFixed(2)}` : '$0.00'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="text-slate-500 text-sm py-2 text-center">No STRONG runners for this algo/scope.</div>
                              )}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Overbet FAV signal performance ($2 win-bet fade on the flagged favorite) */}
              {gtSummary.overbet_fav && (
                <div className="mb-4 bg-gradient-to-r from-red-500/10 to-slate-800/40 border border-red-800/50 rounded-xl p-4" data-testid="gentix-overbet-fav">
                 <div className="flex items-center justify-between flex-wrap gap-3 cursor-pointer" onClick={toggleOverbet} data-testid="overbet-toggle">
                  <div>
                    <div className="text-red-300 text-sm font-bold uppercase tracking-wide flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                      Overbet
                      <span className="text-slate-400 text-xs normal-case font-normal">({showOverbet ? 'hide' : 'show'} details)</span>
                    </div>
                  </div>
                  <div className="flex gap-8">
                    <div className="text-center" data-testid="overbet-count">
                      <div className="text-slate-400 text-xs uppercase tracking-wide"># Flagged</div>
                      <div className="text-2xl font-bold text-white mt-0.5">{gtSummary.overbet_fav.count}</div>
                    </div>
                    <div className="text-center" data-testid="overbet-winpct">
                      <div className="text-slate-400 text-xs uppercase tracking-wide">Fav Win %</div>
                      <div className="text-2xl font-bold text-slate-200 mt-0.5">{gtSummary.overbet_fav.win_pct}%</div>
                    </div>
                    <div className="text-center" data-testid="overbet-roi">
                      <div className="text-slate-400 text-xs uppercase tracking-wide">$2-Win ROI</div>
                      <div className={`text-2xl font-bold mt-0.5 ${gtSummary.overbet_fav.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {gtSummary.overbet_fav.roi >= 0 ? '+' : ''}{gtSummary.overbet_fav.roi}%
                      </div>
                    </div>
                  </div>
                 </div>
                 {showOverbet && (
                   <div className="mt-4 overflow-x-auto" data-testid="overbet-detail-table">
                     {overbetDetail === null ? <div className="text-slate-400 text-sm">Loading…</div> : (
                     <table className="w-full text-xs">
                       <thead><tr className="text-slate-400 border-b border-slate-700 text-left">
                         <th className="py-1 pr-3">Date</th><th className="pr-3">Track</th><th className="pr-3">Race</th><th className="pr-3">Prog</th>
                         <th className="pr-3">Finish</th><th className="pr-3">Proj Odds</th><th className="pr-3">1MTP Odds</th><th className="pr-3">Final Odds</th>
                       </tr></thead>
                       <tbody>
                         {overbetDetail.map((r, i) => (
                           <tr key={i} className="border-b border-slate-800" data-testid={`overbet-row-${i}`}>
                             <td className="py-1 pr-3 text-slate-300">{r.race_date}</td>
                             <td className="pr-3 text-slate-300">{r.track}</td>
                             <td className="pr-3 text-slate-300">{r.race}</td>
                             <td className="pr-3 text-slate-300">{r.prog}</td>
                             <td className={`pr-3 font-semibold ${r.finish === 1 ? 'text-emerald-400' : 'text-slate-300'}`}>{r.finish == null ? 'unpl' : r.finish}</td>
                             <td className="pr-3 text-slate-400">{r.projodds != null ? Number(r.projodds).toFixed(1) : '—'}</td>
                             <td className="pr-3 text-amber-300">{r.odds1mtp != null ? Number(r.odds1mtp).toFixed(1) : '—'}</td>
                             <td className="pr-3 text-slate-300">{r.finalodds != null ? Number(r.finalodds).toFixed(1) : '—'}</td>
                           </tr>
                         ))}
                       </tbody>
                     </table>)}
                   </div>
                 )}
                </div>
              )}

              {rankPerf && (
                <div className="mb-4 bg-slate-800/40 border border-slate-700 rounded-xl p-4" data-testid="rank-performance">
                  <div className="text-slate-200 text-sm font-bold mb-1">Consensus Rank Performance — best-ranked horses</div>
                  <div className="text-slate-400 text-xs mb-3">Win% &amp; flat $2 WIN-bet ROI by consensus rank, both algos ({rankPerf.dates} days)</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {['original', 'logarithmic'].map(al => (
                      <div key={al}>
                        <div className="text-xs font-semibold text-slate-300 mb-1">{al === 'logarithmic' ? 'Logarithmic (consensus2)' : 'Original (consensus)'}</div>
                        <table className="w-full text-xs" data-testid={`rank-table-${al}`}>
                          <thead><tr className="text-slate-400 border-b border-slate-700 text-left">
                            <th className="py-1 pr-2">Rank</th><th className="pr-2 text-right">Races</th><th className="pr-2 text-right">Win %</th><th className="pr-2 text-right">$2 ROI</th><th className="pr-2 text-right">Avg Odds</th>
                          </tr></thead>
                          <tbody>
                            {rankPerf.algos[al].map(r => (
                              <tr key={r.rank} className="border-b border-slate-800" data-testid={`rank-${al}-${r.rank}`}>
                                <td className="py-1 pr-2 text-slate-300 font-semibold">{r.rank}</td>
                                <td className="pr-2 text-right text-slate-400">{r.races}</td>
                                <td className="pr-2 text-right text-amber-300 font-semibold">{r.win_pct}%</td>
                                <td className={`pr-2 text-right font-bold ${r.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{r.roi >= 0 ? '+' : ''}{r.roi}%</td>
                                <td className="pr-2 text-right text-slate-400">{r.avg_odds != null ? r.avg_odds : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {/* Rule Set × Algorithm matrix */}
              <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden" data-testid="gentix-matrix">
                <div className="px-4 py-2.5 text-sm font-semibold text-slate-200 border-b border-slate-700 flex items-center justify-between flex-wrap gap-2">
                  <span>Performance by Rule Set × Algorithm</span>
                  <span className="text-xs text-slate-500 font-normal">Live config highlighted · click a row to load its By-Track below</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-xs uppercase tracking-wide bg-slate-900/40">
                        <th className="text-left px-4 py-2 font-semibold">Config</th>
                        <th className="text-right px-3 py-2 font-semibold"># Races</th>
                        <th className="text-right px-3 py-2 font-semibold"># Keys</th>
                        <th className="text-right px-3 py-2 font-semibold"># Key Win</th>
                        <th className="text-right px-3 py-2 font-semibold"># EX Wins</th>
                        <th className="text-right px-4 py-2 font-semibold">$1 EX ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(gtSummary.matrix || []).map((m) => {
                        const sel = gtCombo && gtCombo.rule_set === m.rule_set && gtCombo.algo === m.algo;
                        return (
                        <tr key={`${m.rule_set}-${m.algo}`}
                          className={`border-t border-slate-800 cursor-pointer hover:bg-slate-800/40 ${m.is_live ? 'bg-amber-500/10' : ''} ${sel ? 'ring-1 ring-inset ring-emerald-600/60' : ''}`}
                          data-testid={`gentix-combo-${m.rule_set}-${m.algo}`}
                          onClick={() => selectCombo(m.rule_set, m.algo)}
                        >
                          <td className={`px-4 py-2 font-medium ${m.is_live ? 'text-amber-200' : 'text-slate-200'}`}>
                            Rule {m.rule_set} · <span className="text-slate-400 font-normal">{algoLabel(m.algo)}</span>
                            {m.is_live && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-600/50">LIVE</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-300">{m.races}</td>
                          <td className="px-3 py-2 text-right text-slate-300">{m.keys}</td>
                          <td className="px-3 py-2 text-right text-slate-300">{m.key_winners}</td>
                          <td className="px-3 py-2 text-right text-slate-300">{m.exacta_wins}</td>
                          <td className={`px-4 py-2 text-right font-bold ${m.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {m.roi >= 0 ? '+' : ''}{m.roi}%
                            <span className="text-slate-500 font-normal text-xs ml-1">(${m.returned?.toFixed(2)}/${m.wagered?.toFixed(2)})</span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* By Track — for the selected Rule×Algo combo */}
              <div className="mt-5 bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden" data-testid="gentix-by-track">
                <div className="px-4 py-2.5 text-sm font-semibold text-slate-200 border-b border-slate-700">
                  By Track — <span className="text-emerald-400">Rule {gtCombo?.rule_set} · {algoLabel(gtCombo?.algo)}</span>
                </div>
                {gtByTrackLoading ? (
                  <div className="text-slate-400 py-6 text-center text-sm">Loading…</div>
                ) : gtByTrack.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-slate-400 text-xs uppercase tracking-wide bg-slate-900/40">
                          <th className="text-left px-4 py-2 font-semibold">Track</th>
                          <th className="text-right px-3 py-2 font-semibold"># Races</th>
                          <th className="text-right px-3 py-2 font-semibold"># Keys</th>
                          <th className="text-right px-3 py-2 font-semibold"># Key Winners</th>
                          <th className="text-right px-3 py-2 font-semibold"># Exacta Wins</th>
                          <th className="text-right px-4 py-2 font-semibold">$1 Exacta ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gtByTrack.map((t) => {
                          const expanded = gtExpandedTrack === t.track;
                          return (
                          <React.Fragment key={t.track}>
                          <tr
                            className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer"
                            data-testid={`gentix-track-${t.track}`}
                            onClick={() => setGtExpandedTrack(expanded ? null : t.track)}
                          >
                            <td className="px-4 py-2 font-mono font-medium text-slate-200">
                              <span className="inline-flex items-center gap-2">
                                <svg className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                {t.track}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-300">{t.races}</td>
                            <td className="px-3 py-2 text-right text-slate-300">{t.keys}</td>
                            <td className="px-3 py-2 text-right text-slate-300">{t.key_winners}</td>
                            <td className="px-3 py-2 text-right text-slate-300">{t.exacta_wins}</td>
                            <td className={`px-4 py-2 text-right font-semibold ${t.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {t.roi >= 0 ? '+' : ''}{t.roi}%
                              <span className="text-slate-500 font-normal text-xs ml-1">(${t.returned?.toFixed(2)}/${t.wagered?.toFixed(2)})</span>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bg-slate-900/50" data-testid={`gentix-track-${t.track}-detail`}>
                              <td colSpan={6} className="px-4 py-3">
                                <div className="overflow-x-auto rounded-lg border border-slate-700">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-400 uppercase tracking-wide bg-slate-900/70">
                                        <th className="text-left px-3 py-1.5 font-semibold">Race #</th>
                                        <th className="text-right px-3 py-1.5 font-semibold"># Keys</th>
                                        <th className="text-right px-3 py-1.5 font-semibold"># Key Winners</th>
                                        <th className="text-right px-3 py-1.5 font-semibold"># Exacta Wins</th>
                                        <th className="text-right px-3 py-1.5 font-semibold">$1 Exacta ROI</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(t.by_race || []).map((rs) => (
                                        <tr key={rs.race_number} className="border-t border-slate-800" data-testid={`gentix-race-${t.track}-${rs.race_number}`}>
                                          <td className="px-3 py-1.5 text-slate-200 font-medium">R{rs.race_number}</td>
                                          <td className="px-3 py-1.5 text-right text-slate-300">{rs.keys}</td>
                                          <td className="px-3 py-1.5 text-right text-slate-300">{rs.key_winners}</td>
                                          <td className="px-3 py-1.5 text-right text-slate-300">{rs.exacta_wins}</td>
                                          <td className={`px-3 py-1.5 text-right font-semibold ${rs.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {rs.roi >= 0 ? '+' : ''}{rs.roi}%
                                            <span className="text-slate-500 font-normal ml-1">(${rs.returned?.toFixed(2)}/${rs.wagered?.toFixed(2)})</span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-slate-500 py-6 text-center text-sm">No races for this combo in range.</div>
                )}
              </div>
              </>
            ) : (
              <div className="text-slate-400 py-8 text-center">No data for this range. Try "Run now" or widen the dates.</div>
            )}
          </div>
        )}


        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <PricingEditor />
        )}

        {/* Feed Processor Tab */}
        {activeTab === 'feed' && (
          <FeedProcessorPanel />
        )}

        {/* Track Maintenance Tab */}
        {activeTab === 'tracks' && (
          <TrackMaintenance />
        )}

        {activeTab === 'liveops' && (
          <LiveOps />
        )}

        {/* Widget Clients Tab */}
        {activeTab === 'clients' && (
          <WidgetClients />
        )}

        {/* Contact Messages Tab */}
        {activeTab === 'messages' && (
          <ContactMessages />
        )}
      </main>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Edit User Access</h3>
            <p className="text-slate-400 mb-4">
              Editing <span className="text-emerald-400 font-medium">{editingUser.email}</span>
            </p>

            <div className="mb-4 text-sm bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2" data-testid="edit-terms-accepted">
              <span className="text-slate-400">Terms &amp; Conditions accepted: </span>
              {editingUser.terms_accepted_at ? (
                <span className="text-emerald-400 font-medium">{formatDateTime(editingUser.terms_accepted_at)}</span>
              ) : (
                <span className="text-slate-500">Not recorded (registered before T&amp;C gate)</span>
              )}
            </div>

            <div className="mb-4 text-sm bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2" data-testid="edit-referral-source">
              <span className="text-slate-400">How did you hear about us: </span>
              {(() => {
                const src = editingUser.referral_source;
                const detail = editingUser.referral_source_detail;
                if (!src && !detail) return <span className="text-slate-500">Not recorded</span>;
                const txt = src === 'Other' && detail ? `Other — ${detail}` : (src || detail);
                return <span className="text-emerald-400 font-medium">{txt}</span>;
              })()}
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Subscription Status
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="none">None</option>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Trial Ends On
                </label>
                <input
                  type="date"
                  value={editTrialDate}
                  onChange={(e) => setEditTrialDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  For users with "trial" status
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Subscription Ends On
                </label>
                <input
                  type="date"
                  value={editExpiryDate}
                  onChange={(e) => setEditExpiryDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  For users with "active" status. This is checked for access control.
                </p>
              </div>
              
              {/* Password Reset Section */}
              <div className="border-t border-slate-600 pt-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Force Password Reset
                </label>
                {editingUser?.force_password_reset ? (
                  <div className="flex items-center gap-3">
                    <div className="px-4 py-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 font-medium rounded-lg">
                      Awaiting Password Reset
                    </div>
                    <button
                      onClick={handleClearPasswordReset}
                      disabled={resetPasswordLoading}
                      className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-300 text-sm font-medium rounded-lg transition"
                    >
                      {resetPasswordLoading ? '...' : 'Clear'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleForcePasswordReset}
                    disabled={resetPasswordLoading}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                  >
                    {resetPasswordLoading ? 'Processing...' : 'Force Password Change'}
                  </button>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  {editingUser?.force_password_reset 
                    ? 'User must change password on next login. Click Clear to cancel.'
                    : 'User will be prompted to set a new password on their next login.'}
                </p>

                <div className="mt-4 pt-4 border-t border-slate-700/60">
                  <button
                    onClick={handleSendResetEmail}
                    disabled={sendResetEmailLoading}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
                    data-testid="send-reset-email-button"
                  >
                    {sendResetEmailLoading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                  <p className="text-xs text-slate-500 mt-2">
                    Emails {editingUser?.email} a secure link to set a new password (expires in 1 hour). Works even if they never log in.
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-700/60">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Set Temporary Password
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={tempPasswordInput}
                      onChange={(e) => setTempPasswordInput(e.target.value)}
                      placeholder="Leave blank to auto-generate"
                      className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 text-white text-sm rounded-lg focus:outline-none focus:border-emerald-500"
                      data-testid="temp-password-input"
                    />
                    <button
                      onClick={handleSetTempPassword}
                      disabled={tempPasswordLoading}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition whitespace-nowrap"
                      data-testid="set-temp-password-button"
                    >
                      {tempPasswordLoading ? 'Setting...' : 'Set Temp Password'}
                    </button>
                  </div>
                  {tempPasswordResult && (
                    <div className="mt-3 px-3 py-2 bg-indigo-500/10 border border-indigo-500/40 rounded-lg" data-testid="temp-password-result">
                      <p className="text-xs text-slate-400 mb-1">Temporary password (copy &amp; share securely):</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-indigo-300 font-mono text-sm break-all select-all">{tempPasswordResult}</code>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(tempPasswordResult);
                              setTempPasswordCopied(true);
                              setTimeout(() => setTempPasswordCopied(false), 2000);
                            } catch (e) {
                              setError('Could not copy to clipboard');
                            }
                          }}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-md transition whitespace-nowrap"
                          data-testid="copy-temp-password-button"
                        >
                          {tempPasswordCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    Sets a temporary password for {editingUser?.email} and forces them to change it on next login. The plaintext is shown once here only.
                  </p>
                </div>
              </div>

              {/* Affiliate Status */}
              <div className="border-t border-slate-600 pt-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Affiliate Status
                </label>
                {editingUser?.is_affiliate ? (
                  <div className="flex items-center gap-3">
                    <div className="px-3 py-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 font-medium rounded-lg">
                      {editingUser.is_affiliate}
                    </div>
                    <button
                      onClick={removeAffiliateFromEdit}
                      data-testid="edit-remove-affiliate-btn"
                      className="px-3 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-sm font-medium rounded-lg transition"
                    >
                      Remove Affiliate
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">This user is not an affiliate.</p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Removing revokes this user's affiliate ID and referral tracking.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-between">
              <button
                onClick={() => handleDeleteUser(editingUser)}
                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 font-medium rounded-lg transition"
              >
                Delete User
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Assignment Modal */}
      {showAffiliateModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              {affiliateAction === 'assign' ? 'Assign Affiliate' : 'Link as Affiliate'}
            </h3>
            <p className="text-slate-400 mb-4">
              {affiliateAction === 'assign' 
                ? <>Track <span className="text-emerald-400 font-medium">{selectedUser.email}</span> under an affiliate</>
                : <>Make <span className="text-emerald-400 font-medium">{selectedUser.email}</span> an affiliate account</>
              }
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Affiliate ID
              </label>
              <select
                value={selectedAffid}
                onChange={(e) => setSelectedAffid(e.target.value)}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">-- Select Affiliate --</option>
                {affiliates.map((aff) => (
                  <option key={aff.affid} value={aff.affid}>
                    {aff.affid} - {aff.name} ({aff.commission_pct}%)
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowAffiliateModal(false); setSelectedUser(null); setSelectedAffid(''); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAffiliateAction}
                disabled={!selectedAffid}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
              >
                {affiliateAction === 'assign' ? 'Assign' : 'Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Affiliate Modal */}
      {showCreateAffiliateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Create New Affiliate</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Affiliate ID *
                </label>
                <input
                  type="text"
                  value={newAffiliate.affid}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, affid: e.target.value.toUpperCase() })}
                  placeholder="e.g., AFF002"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newAffiliate.name}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, name: e.target.value })}
                  placeholder="e.g., John's Racing Tips"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Commission %
                </label>
                <input
                  type="number"
                  value={newAffiliate.commission_pct}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, commission_pct: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Email (optional)
                </label>
                <input
                  type="email"
                  value={newAffiliate.contact_email}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, contact_email: e.target.value })}
                  placeholder="affiliate@example.com"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCreateAffiliateModal(false); setNewAffiliate({ affid: '', name: '', commission_pct: 10, contact_email: '' }); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAffiliate}
                disabled={!newAffiliate.affid || !newAffiliate.name}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
              >
                Create Affiliate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Affiliate Modal */}
      {showEditAffiliateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md" data-testid="edit-affiliate-modal">
            <h3 className="text-xl font-semibold text-white mb-1">Edit Affiliate</h3>
            <p className="text-sm text-slate-400 mb-4">
              ID <span className="font-mono text-amber-400">{editAffiliate.affid}</span> can't be changed (linked accounts depend on it).
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Name *</label>
                <input
                  type="text"
                  value={editAffiliate.name}
                  onChange={(e) => setEditAffiliate({ ...editAffiliate, name: e.target.value })}
                  data-testid="edit-affiliate-name"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Commission %</label>
                <input
                  type="number"
                  value={editAffiliate.commission_pct}
                  onChange={(e) => setEditAffiliate({ ...editAffiliate, commission_pct: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  data-testid="edit-affiliate-commission"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Contact Email (optional)</label>
                <input
                  type="email"
                  value={editAffiliate.contact_email}
                  onChange={(e) => setEditAffiliate({ ...editAffiliate, contact_email: e.target.value })}
                  placeholder="affiliate@example.com"
                  data-testid="edit-affiliate-email"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Sign-up Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${(process.env.REACT_APP_PUBLIC_APP_URL || 'https://intellitix.ai').replace(/\/$/, '')}/signup?affid=${editAffiliate.affid}`}
                    data-testid="edit-affiliate-signup-link"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-emerald-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const link = `${(process.env.REACT_APP_PUBLIC_APP_URL || 'https://intellitix.ai').replace(/\/$/, '')}/signup?affid=${editAffiliate.affid}`;
                      navigator.clipboard?.writeText(link);
                      setSuccess('Sign-up link copied');
                    }}
                    data-testid="copy-affiliate-signup-link"
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Signups through this link are auto-attributed to {editAffiliate.affid}.</p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEditAffiliateModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAffiliate}
                disabled={!editAffiliate.name}
                data-testid="save-affiliate-btn"
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}


      {/* User Detail Panel */}
      {showUserDetail && detailUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">{detailUser.email}</h3>
                <p className="text-slate-400">{detailUser.name || 'No name set'}</p>
              </div>
              <button
                onClick={() => { setShowUserDetail(false); setDetailUser(null); setUserActivity([]); setLoginEvents([]); setTxns(null); }}
                className="text-slate-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* User Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Status</p>
                <span className={`inline-block mt-1 px-2 py-1 text-sm font-medium rounded border ${getStatusBadge(detailUser.subscription_status)}`}>
                  {detailUser.subscription_status || 'none'}
                </span>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Plan</p>
                <p className="text-white font-medium mt-1">{detailUser.subscription_plan || '-'}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Expires</p>
                <p className="text-white font-medium mt-1">{formatDate(detailUser.subscription_ends_at)}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Signed Up</p>
                <p className="text-white font-medium mt-1">{formatDate(detailUser.created_at)}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Referred By</p>
                <p className="text-purple-400 font-medium mt-1">{detailUser.referred_by || '-'}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-slate-500 text-sm">Is Affiliate</p>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-amber-400 font-medium">{detailUser.is_affiliate || '-'}</p>
                  {detailUser.is_affiliate && (
                    <button
                      onClick={removeAffiliateFromDetail}
                      data-testid="detail-remove-affiliate-btn"
                      className="px-2 py-1 text-xs font-medium rounded border border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition"
                      title="Remove affiliate status from this user"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {detailUser.granted_by_admin && (
                <div className="bg-slate-900 rounded-lg p-4 col-span-2">
                  <p className="text-slate-500 text-sm">Granted By</p>
                  <p className="text-emerald-400 font-medium mt-1">{detailUser.granted_by_admin}</p>
                </div>
              )}
            </div>
            
            {/* Billing / Transactions */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-3">
                Billing / Transactions
                {txns?.totals?.count > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({txns.totals.count} charges · ${txns.totals.revenue.toFixed(2)}
                    {txns.totals.commission > 0 ? ` · commission $${txns.totals.commission.toFixed(2)}` : ''})
                  </span>
                )}
              </h4>
              {loadingTxns ? (
                <p className="text-slate-400">Loading transactions...</p>
              ) : !txns || txns.transactions.length === 0 ? (
                <p className="text-slate-500 bg-slate-900 rounded-lg p-4">
                  No billing records found{txns && !txns.user.has_stripe_customer && !txns.user.referred_by ? ' (no Stripe customer or affiliate ledger for this user).' : '.'}
                </p>
              ) : (
                <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="user-transactions">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left px-3 py-2 font-medium">Date</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-right px-3 py-2 font-medium">Commission</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-left px-3 py-2 font-medium">Source</th>
                        <th className="text-right px-3 py-2 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.transactions.map((t, i) => (
                        <tr key={t.id || i} className="border-b border-slate-800/60">
                          <td className="px-3 py-2 text-slate-300">{formatDate(t.occurred_at)}</td>
                          <td className="px-3 py-2 text-right text-slate-200">${(t.amount || 0).toFixed(2)} {t.currency}</td>
                          <td className="px-3 py-2 text-right text-emerald-400">{t.commission_amount != null ? `$${t.commission_amount.toFixed(2)}` : '-'}</td>
                          <td className="px-3 py-2 text-slate-400">{t.status || '-'}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 text-xs rounded border ${t.source === 'stripe' ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40' : 'bg-slate-700/40 text-slate-400 border-slate-600'}`}>
                              {t.source === 'stripe' ? 'Stripe' : 'Ledger'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {t.url ? <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">View</a> : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Logins (account-sharing review) */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-3">
                Recent Logins
                {loginEvents.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({new Set(loginEvents.filter(e => e.ip).map(e => e.ip)).size} distinct IPs)
                  </span>
                )}
              </h4>
              {loadingLoginEvents ? (
                <p className="text-slate-400">Loading logins...</p>
              ) : loginEvents.length === 0 ? (
                <p className="text-slate-500 bg-slate-900 rounded-lg p-4">No login events recorded yet.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="login-events-list">
                  {loginEvents.map((ev, i) => {
                    const loc = [ev.city, ev.region, ev.country].filter(Boolean).join(', ');
                    return (
                      <div key={i} className="bg-slate-900 rounded-lg p-3 flex justify-between items-center gap-3">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-mono">{ev.ip || 'unknown IP'}</p>
                          <p className="text-slate-500 text-xs truncate" title={ev.user_agent}>
                            {loc || 'Unknown location'}{ev.user_agent ? ` · ${ev.user_agent}` : ''}
                          </p>
                        </div>
                        <p className="text-slate-400 text-xs whitespace-nowrap">{formatDateTime(ev.created_at)}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Activity Log */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-3">Activity Log</h4>
              {loadingActivity ? (
                <p className="text-slate-400">Loading activity...</p>
              ) : userActivity.length === 0 ? (
                <p className="text-slate-500 bg-slate-900 rounded-lg p-4">No activity recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {userActivity.map((activity, i) => (
                    <div key={i} className="bg-slate-900 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white">{activity.action}</p>
                        <p className="text-slate-500 text-sm">{activity.details}</p>
                      </div>
                      <p className="text-slate-400 text-sm">{formatDateTime(activity.timestamp)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Summary Modal */}
      {showAffSummary && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => { setShowAffSummary(false); setAffSummary(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="affiliate-summary-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-800 sticky top-0 bg-slate-900">
              <div>
                <h3 className="text-xl font-bold text-white">
                  Affiliate Summary
                  {affSummary?.affiliate && (
                    <span className="ml-2 px-2 py-0.5 text-sm font-mono rounded bg-amber-500/20 text-amber-400 border border-amber-500/50">{affSummary.affiliate.affid}</span>
                  )}
                </h3>
                {affSummary?.affiliate?.name && <p className="text-slate-400 text-sm mt-1">{affSummary.affiliate.name} · {affSummary.affiliate.commission_pct}% commission</p>}
              </div>
              <div className="flex items-center gap-3">
                {affSummary && (
                  <button
                    onClick={exportAffiliateSummaryCsv}
                    data-testid="affiliate-summary-export-csv"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition"
                    title="Download this affiliate's earnings statement as CSV"
                  >
                    ⬇ Export CSV
                  </button>
                )}
                <button onClick={() => { setShowAffSummary(false); setAffSummary(null); }} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
              </div>
            </div>
            <div className="p-5">
              {loadingAffSummary ? (
                <p className="text-slate-400">Loading summary...</p>
              ) : !affSummary ? (
                <p className="text-slate-500">Could not load affiliate data.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="bg-slate-800/60 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Signups</p>
                      <p className="text-white text-2xl font-bold">{affSummary.stats.total_signups}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Paying Customers</p>
                      <p className="text-white text-2xl font-bold">{affSummary.stats.paying_customers}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Revenue</p>
                      <p className="text-emerald-400 text-2xl font-bold">${affSummary.stats.estimated_revenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3">
                      <p className="text-slate-400 text-xs">Commission</p>
                      <p className="text-emerald-400 text-2xl font-bold">${affSummary.stats.estimated_commission.toFixed(2)}</p>
                    </div>
                  </div>

                  <h4 className="text-white font-semibold mb-2">Referred Users ({affSummary.users.length})</h4>
                  <div className="bg-slate-800/40 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-700">
                          <th className="text-left px-3 py-2 font-medium">Email</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                          <th className="text-right px-3 py-2 font-medium">Charges</th>
                          <th className="text-right px-3 py-2 font-medium">Revenue</th>
                          <th className="text-right px-3 py-2 font-medium">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affSummary.users.length === 0 ? (
                          <tr><td colSpan="5" className="px-3 py-6 text-center text-slate-500">No referred users yet.</td></tr>
                        ) : affSummary.users.map((u) => (
                          <tr key={u.id} className="border-b border-slate-800/60">
                            <td className="px-3 py-2 text-slate-200">{u.email}</td>
                            <td className="px-3 py-2 text-slate-400">{u.subscription_status || '-'}</td>
                            <td className="px-3 py-2 text-right text-slate-300">{u.charges || 0}</td>
                            <td className="px-3 py-2 text-right text-slate-200">${(u.revenue || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-emerald-400">${(u.commission || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
